// 편집 캔버스 A4 낱장 페이지 계획(순수). DOM 측정(EditorPage measure())과 분리해 단위 테스트 가능하게 한다.
//
// PagedEditBody의 measure()가 렌더된 DOM에서 노드/표 행 높이·안전 경계(computeSafeCutRows)를 측정한 뒤
// 이 함수로 페이지별 배치(PagedItem[][])를 계산한다. 여기엔 DOM/React 의존이 없다.

/** 한 낱장에 배치되는 항목: 노드 통짜(rowRange 없음) 또는 표의 행 범위 / repeat의 항목 범위 조각. */
export type PagedItem = {
  nodeId: string;
  /**
   * **인스턴스 경로**(선택) — 이 지시가 겨냥하는 노드를 감싼 repeat 항목들의 경로
   * (`{repeatId}::{항목인덱스}`를 바깥→안쪽으로 `/`로 이은 것). 미지정 = 최상위 body 노드(현행 전부).
   *
   * 중첩 반복(반복 안 반복)에서 안쪽 repeat은 **노드 1개인데 인스턴스가 바깥 항목 수만큼**이라
   * nodeId 전역 키로는 "사진 3번 안의 발견사항 0~2건"을 가리킬 수 없다. path가 붙으면 렌더 조회 키가
   * `pagedItemKey()` = `path/nodeId`가 되어 인스턴스별로 다른 범위를 지시할 수 있다.
   *
   * ⚠️ path가 있는 항목은 **낱장 body slice에 넣지 않는다**(최상위 노드가 아니다) — 부모 최상위
   * 항목이 같은 낱장에 함께 있어야 하고, 렌더는 그 서브트리를 타고 내려가며 이 지시를 소비한다.
   * buildPageChannels()가 그 분리를 담당한다.
   */
  path?: string;
  rowRange?: [number, number];
  /** repeat 노드의 항목 범위 [startItem, endItem)(반열림) — 렌더가 그 항목만 렌더(RenderContext.itemRanges). */
  itemRange?: [number, number];
  /**
   * 순차 다경계(결정 2026-09-03 심야) — 이 낱장에서 보이는 **단위 흐름 청크 창**
   * [startChunk, endChunk)(반열림, `unitFlowChunks` 배열 인덱스). `path` = 그 단위의 항목
   * 인스턴스 경로, `nodeId` = 그 단위를 낳은 repeat 노드(조회 키 유일성용 — 소비는 경로로).
   * 렌더(RepeatItem)가 `RenderContext.chunkRanges[path]`로 소비해 창의 청크만 남긴 조각을
   * 그린다(`pruneToChunkWindow`). 경계 청크의 행/항목 클립은 기존 rowRanges/itemRanges 그대로.
   */
  chunkRange?: [number, number];
  /** 채움 노드에 적용할 계산된 높이(px). 렌더가 이 높이를 노드에 적용. */
  fillHeight?: number;
  /** 채움 표(pageFill 표)에 전달할 잔여 slack(px). 렌더가 빈 괘선 행으로 페인팅(RenderContext.fillSlacks). */
  fillSlack?: number;
  /**
   * 이 조각이 **저작 구조대로 배치되지 못했다**는 사실 신호(표시 전용 — 렌더 채널이 아니다).
   * 저작 옵션 위반 알림이 아니라 "묶음이 페이지에 안 들어가 이렇게 됐다"는 결과 통지이고,
   * 저작자는 이걸 보고 구조·크기(사진 높이·묶음 구성)를 고친다(결정 2026-08-18 결론 4·5).
   *  - `'split'`: 단위가 페이지에 안 들어가 **한 단계 안(안쪽 반복 항목) 경계에서 강제로 쪼갬**.
   *  - `'lead-only'`: 고정분(사진 등)이 커서 그 낱장에 **안쪽 항목이 하나도 못 들어감** — 고정분만
   *    배치하고 안쪽 항목은 다음 낱장부터. 넘치지는 않는다(넘치며 밀어 넣는 것보다 이쪽이 낫다).
   *  - `'overflow'`: 자를 경계가 없거나 고정분 자체가 페이지를 초과 → 단독 배치 + 넘침 허용.
   * 둘 다 해당하면 **overflow가 이긴다**(더 심한 사실 — 쪼갰는데도 여전히 넘쳤다는 뜻).
   * P6-2(저작 화면 경고)가 소비한다 — 이번 범위에서는 계획에 담기만 한다.
   */
  forcedPlacement?: ForcedPlacement;
  /**
   * 이 지시가 겨냥한 **부모 항목**(= `path`)이 이 낱장에서는 **조각 continuation**이라는 표식 —
   * 그 항목의 lead(사진·설명 등, 안쪽 반복으로 가는 체인 밖 전부)를 그리지 않는다.
   *
   * lead는 **첫 조각에만** 나온다. 조각마다 통째로 다시 그리면 사진이 두 장에 되풀이되고, 그건
   * 사용자가 기각한 안이다(결정 2026-08-18 대안 D "내용 중복"). `path`가 있는 지시에만 붙는다
   * (최상위 항목은 자기 자신이 흐름의 단위라 lead 개념이 없다).
   *
   * 렌더는 `buildPageChannels`가 만든 `RenderContext.leadSuppressed[path]`로 소비한다.
   */
  suppressLead?: true;
};

/** 강제 배치 사실(표시 전용) — `PagedItem.forcedPlacement` 참조. */
export type ForcedPlacement = 'split' | 'lead-only' | 'overflow';

/**
 * 강제 신호의 세기 — 한 낱장의 같은 키 지시가 합쳐질 때 **더 심한 사실**을 남기기 위한 순서
 * (placeUnits가 'split'을 'overflow'로 덮어쓰는 규약과 같은 방향: 쪼개고도 넘쳤다 > 고정분만 놓았다
 * > 쪼갰다 > 신호 없음). 표시 전용이라 배치에는 관여하지 않는다.
 */
const FORCED_RANK: Record<ForcedPlacement, number> = { split: 1, 'lead-only': 2, overflow: 3 };

/** 두 강제 신호 중 더 강한 쪽(둘 다 없으면 undefined). */
function strongerForced(
  a: ForcedPlacement | undefined,
  b: ForcedPlacement | undefined,
): ForcedPlacement | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return FORCED_RANK[b] > FORCED_RANK[a] ? b : a;
}

/**
 * PagedItem이 겨냥하는 노드의 **렌더 조회 키** — 렌더(RenderContext)가 이 키로 자기 지시를 찾는다.
 * 최상위(path 없음)는 nodeId 그대로라 기존 계획과 완전 동일하다(회귀 0).
 */
export function pagedItemKey(item: Pick<PagedItem, 'nodeId' | 'path'>): string {
  return item.path ? `${item.path}/${item.nodeId}` : item.nodeId;
}

/** 한 낱장의 PagedItem[]에서 렌더에 넘길 채널 맵 + 최상위 슬라이스를 만든 결과. */
export interface PageChannels {
  /** 이 낱장의 **최상위** 항목(path 없음) — 호스트가 body slice(노드 배열)를 만드는 근거. */
  topLevel: PagedItem[];
  rowRanges: Record<string, [number, number]>;
  itemRanges: Record<string, [number, number]>;
  fillHeights: Record<string, number>;
  fillSlacks: Record<string, number>;
  /**
   * 순차 다경계 청크 창 — 키는 **항목 인스턴스 경로**(`PagedItem.path` 그대로, leadSuppressed와
   * 같은 규약 — 소비자가 그 항목을 그리는 RepeatItem이라서다). 값은 `unitFlowChunks` 인덱스 창.
   */
  chunkRanges: Record<string, [number, number]>;
  /**
   * 조각 continuation 표식 — 키는 **항목 인스턴스 경로**(`PagedItem.path` 그대로, RepeatItem의
   * instancePath와 같은 문자열)이고, 다른 채널처럼 `pagedItemKey`가 아니다. 소비자가 repeat 노드가
   * 아니라 **그 항목을 그리는 RepeatItem**이기 때문이다.
   */
  leadSuppressed: Record<string, true>;
}

/**
 * 낱장 항목들 → RenderTemplate에 넘길 채널 맵(순수). 키는 pagedItemKey(인스턴스 경로 포함)라
 * 중첩 반복의 인스턴스별 지시가 서로 덮어쓰지 않는다.
 *
 * 호스트 2곳(admin PagedEditBody / rc PagedComposerView)이 각자 손으로 4개 맵을 만들던 것을
 * 한 곳으로 모은 것 — 채널이 늘거나 키 규칙이 바뀔 때 한쪽만 고쳐 갈라지지 않게 한다.
 */
export function buildPageChannels(items: PagedItem[]): PageChannels {
  const out: PageChannels = {
    topLevel: [],
    rowRanges: {},
    itemRanges: {},
    fillHeights: {},
    fillSlacks: {},
    chunkRanges: {},
    leadSuppressed: {},
  };
  for (const it of items) {
    const key = pagedItemKey(it);
    if (it.path === undefined) out.topLevel.push(it);
    if (it.rowRange) out.rowRanges[key] = it.rowRange;
    if (it.itemRange) out.itemRanges[key] = it.itemRange;
    if (it.fillHeight !== undefined) out.fillHeights[key] = it.fillHeight;
    if (it.fillSlack !== undefined) out.fillSlacks[key] = it.fillSlack;
    // 청크 창은 **항목 경로**로 키를 잡는다(leadSuppressed와 같은 이유 — PageChannels 주석).
    if (it.chunkRange && it.path !== undefined) out.chunkRanges[it.path] = it.chunkRange;
    // lead 억제는 **항목 경로**로 키를 잡는다(pagedItemKey 아님 — 위 PageChannels 주석 참조).
    if (it.suppressLead === true && it.path !== undefined) out.leadSuppressed[it.path] = true;
  }
  return out;
}

/** 표 노드의 (클립 무관) 측정 결과 — 페이지 계획 입력. */
export interface MeasuredTable {
  /** 전개 후 총 행 수(rowRanges 클립 무관). */
  totalRows: number;
  /** 표 전체 높이(px). */
  tableHeight: number;
  /** 행 r의 top offset(px). r===totalRows면 tableHeight. */
  rowTop: (r: number) => number;
  /** computeSafeCutRows 결과(길이 totalRows+1). keep-together 그룹 관통 경계만 false, 나머지 true. */
  safe: boolean[];
  /** 표가 남은 공간에 통째로 안 들어가면 새 페이지에서 시작. */
  startOnNewPage: boolean;
}

/**
 * 반복 단위(항목) 하나 안의 **한 단계 안쪽 분할 경계** — 그 단위가 새 페이지에도 통째로 안 들어갈
 * 때만 소비된다(들어가면 통짜 = 현행 그대로, 회귀 0).
 *
 * 경계는 **안쪽 repeat의 항목**(결정 2026-08-18 경계 우선순위 1) 또는 **안쪽 표의 행**(우선순위 3)
 * 이다 — 새 기준을 발명한 게 아니라 구조가 이미 제공하는 자연 경계이고, 특정 위젯에 대한 분기가
 * 아니다. 깊이 무관: 안쪽 repeat이 또 unitInner를 가지면 같은 규칙이 한 단계 더 내려간다
 * (2단 특수 분기 없음). 표 축은 한 단계로 끝난다(`MeasuredInnerAxis` 주석).
 *
 * ⚠️ 표 축에 `tablePolicy`는 **읽지 않는다** — 최상위 표도 정책과 무관하게 "들어가면 통짜, 안
 * 들어가면 행 분할"이라(planEditPages 행 분할 경로), 중첩만 정책을 읽으면 두 자리가 갈린다.
 * `tablePolicy: 'whole'`은 브라우저 네이티브 인쇄(`breakInside`)용 축이고 낱장 엔진과 별개다.
 */
export type MeasuredRepeatInner = MeasuredRepeatInnerBase & MeasuredInnerAxis;

/**
 * 경계의 **단위 축** — 정확히 하나만 채운다.
 *  - `repeat`: 안쪽 반복의 **항목**이 단위(우선순위 1). 조각 지시는 `itemRange`로 나간다.
 *  - `table`: 안쪽 표의 **행**이 단위(우선순위 3). 조각 지시는 `rowRange`로 나간다 — 렌더는
 *    이미 `rowRanges`를 인스턴스 경로 키(`{항목경로}/{표id}`)로 조회하므로 새 채널이 필요 없다.
 *
 * 표 축은 **한 단계로 끝난다**(표 행 밑으로는 더 내려가지 않는다 — 셀 안 반복은 페이지 흐름
 * 경계가 아니다). repeat 축만 `unitInner`로 재귀한다.
 */
export type MeasuredInnerAxis =
  | { repeat: MeasuredRepeat; table?: undefined }
  | { table: MeasuredTable; repeat?: undefined };

/** 축 무관 공통분 — `MeasuredRepeatInner` 참조. */
export interface MeasuredRepeatInnerBase {
  /** 안쪽 경계 노드 id(repeat 또는 표) — 조각의 PagedItem.nodeId. */
  nodeId: string;
  /**
   * 이 안쪽 인스턴스를 감싼 **바깥 항목 경로**(`{repeatId}::{항목인덱스}`를 바깥→안쪽 `/`로 이은 것)
   * — 조각의 PagedItem.path. `pagedItemKey`가 이걸 `path/nodeId`로 합쳐 렌더 조회 키를 만든다.
   */
  path: string;
  /**
   * 항목의 **고정분**(lead) 높이(px) — 사진·설명 등 + 그 앞뒤 gap. **첫 조각에만** 렌더된다.
   * 측정은 렌더가 방출한 `data-composer-lead` 마커로 lead 보유 조각을 특정한 뒤 그 조각 안에서
   * (항목 − 안쪽 컨테이너)를 뺀 값이라 조각 수·조각 경계에 불변이다(P6-1 1단계 불변식 ②).
   */
  leadHeight: number;
  /**
   * continuation 조각에도 남는 체인 컨테이너 chrome(px) — 조각 높이 = C + 그 조각의 단위 합.
   *
   * ⚠️ **통짜 상태에서는 관측 자체가 불가능하다**(잴 continuation 조각이 없다) → undefined.
   * 그때 0으로 **추정**하되, 그 추정이 위험하지 않은 이유는 계획기가 **C를 분할 여부 판단에 절대
   * 쓰지 않기** 때문이다(아래 placeUnits 주석) — C가 나중에 관측돼도 분할 결정이 뒤집히지
   * 않으므로 통짜↔분할 진동이 성립하지 않는다. 1차 실패는 이 축을 아예 두지 않아 continuation이
   * 항상 C만큼 넘치던 것이다.
   */
  contHeight?: number;
  /**
   * 이 경계 **자신이 채움 표**(`MeasuredRepeat.fillTable === 경계 표 id)라는 신호 — 채움 표가
   * 단위의 유일한 표라 분할 경계를 겸하는 형상(table 축 전용). 계획은 이 축의 조각이 **전 행을
   * 한 조각으로** 담을 때만(lead-only 후퇴 뒤 새 낱장 등) 잔여를 채움으로 부여하고, 행 분할된
   * 조각에는 부여하지 않는다(degrade — 결정 2026-09-03). 채움 표가 lead에 속하는 형상(실사용
   * TBM: 경계는 교육기록 repeat)은 이 신호가 아니라 `AncestorChrome.fillTable` 경로가 담당한다.
   */
  fill?: true;
}

/**
 * 경계 축의 **통짜 높이**(px) — 단위 높이 합 + 단위 사이 gap. 측정(pageMeasure)이 항목 높이를
 * `lead + 이 값`으로 재구성할 때와 계획이 총높이를 볼 때가 같은 식이어야 하므로 한 곳에 둔다.
 */
export function innerAxisTotalH(inner: MeasuredInnerAxis): number {
  if (inner.repeat) {
    const rm = inner.repeat;
    const sum = rm.unitHeights.reduce((a, b) => a + b, 0);
    return sum + rm.gap * Math.max(0, rm.unitHeights.length - 1);
  }
  return inner.table.tableHeight;
}

/**
 * 경계 축의 **단위 수**(반복=단위(행/항목) 개수, 표=행 수). `innerAxisTotalH`가 몇 개의 측정치를
 * 더해 만든 합인지를 알려주는 짝 — 그 합을 컨테이너 실측과 대조할 때 **반올림 허용치**를 정하는 데
 * 쓴다(offsetHeight는 정수 반올림이라 항이 많을수록 합의 오차 상한이 커진다).
 */
export function innerAxisUnitCount(inner: MeasuredInnerAxis): number {
  return inner.repeat ? inner.repeat.unitHeights.length : inner.table.totalRows;
}

/** repeat 노드의 (클립 무관) 측정 결과 — 항목 단위 페이지 분할 입력(MeasuredTable과 대칭). */
export interface MeasuredRepeat {
  /** 원본 총 항목 수(itemRanges 클립 무관). */
  totalItems: number;
  /** 다단 단 수(미지정/1 = 세로 나열). 분할 단위는 행(=columns개 묶음) — 같은 행 항목은 함께 이동. */
  columns: number;
  /**
   * 분할 단위 u의 높이(px). 다단이면 행 높이(그 행 항목들의 max, grid 행 동형), 세로 나열이면 항목
   * 높이. 길이 = ceil(totalItems / columns). 항목은 통째 유지(중간 자르기 없음 — 카드 판독성).
   */
  unitHeights: number[];
  /** 단위(행/항목) 사이 간격(px) — repeat gap. 마지막 단위 뒤에는 없음. */
  gap: number;
  /** 반복 전체가 남은 공간에 통째로 안 들어가면 새 페이지에서 시작. */
  startOnNewPage: boolean;
  /**
   * 단위별 안쪽 분할 경계(선택, 길이 = unitHeights.length). 경계가 없는 단위는 undefined =
   * 현행 그대로(통짜 또는 넘침). **다단(columns≥2)에는 두지 않는다** — 단위가 항목 여러 개라
   * 어느 항목의 안쪽을 자를지가 모호하다(측정이 그 축을 만들지 않는다).
   */
  unitInner?: (MeasuredRepeatInner | undefined)[];
  /**
   * 순차 다경계(결정 2026-09-03 심야) — 단위별 **흐름 청크 측정치**(선택, 길이 = unitHeights.length).
   * `findSoleNestedBoundary`가 null(경계 후보 여럿)이고 청크열에 분할 가능 청크(table/repeat)가
   * 있을 때만 측정이 세운다 — 유일 경계 형상은 종전 unitInner 경로 그대로(회귀 0, 상호 배타).
   * 계획은 빈 낱장에도 안 들어가는 단위를 이 청크열로 **최상위 body 동형** 순차 배치한다
   * (placeChunks). **다단(columns≥2)에는 세우지 않는다**(unitInner와 같은 규약).
   */
  unitChunks?: (MeasuredUnitChunks | undefined)[];
  /**
   * 단위 template 청크열(unitFlowChunks)에 pageBreak:'before' **선언** 청크가 존재(2026-09-05) —
   * **template 파생 신호로 전 단위·전 측정 회차에 동일하다**(측정 산출물이 아니다). 계획은 이
   * 신호만으로 단위를 청크 경로에 라우팅한다.
   *
   * ⚠️ 측정된 청크 높이(h>0)로 라우팅을 게이트하면 안 된다 — 높이는 클립·비활성 슬롯·수렴 중간
   * 상태에 따라 **유닛별로** 달라져, 일부 유닛만 슬라이스로 빠진 계획이 수렴 가드의 순환 감지에
   * 걸려 그대로 고착된다(2026-09-05 dev 실측: 세션 3개 중 1·2만 break 무시된 채 안정). 실제
   * newPage 발동은 placeChunks의 h>0 게이트가 거른다 — 비활성 슬롯의 h=0 break 청크는 창만
   * 실리고 분할은 없다(무해).
   */
  breakChunkDecl?: true;
  /**
   * 단위 template 안의 **유일한 채움 표**(`layout.pageFill` 표) id — 반복 단위 안 낱장 채움
   * (결정 2026-09-03, `findSoleUnitFillTable` 산출). 있으면 계획은 이 반복을 **통째로 들어가도
   * 단위 배치**로 다루고, 채움 표가 통짜로 렌더되는 낱장(단위 통짜 낱장 / 분할 시 lead 낱장)의
   * 잔여를 `{path: 단위경로, nodeId: 이 표}` fillSlack으로 부여해 낱장을 소진한다 — 뒤 단위·뒤
   * 노드는 자연히 다음 낱장부터. **다단(columns≥2)에는 세우지 않는다**(단위 경로가 항목 하나와
   * 일대일이 아니다 — unitInner와 같은 규약). 없으면(0개 또는 2개+) 종전 그대로(회귀 0).
   */
  fillTable?: string;
}

/**
 * 한 반복 단위의 흐름 청크 측정치 — 순차 다경계(`MeasuredRepeat.unitChunks`) 입력.
 *  - chunks: `unitFlowChunks` 순서와 **인덱스 정렬**된 청크별 측정(MeasuredNode 재사용 — 구간은
 *    nodeHeight, 경계는 table/repeat 축, 채움 표 청크는 pageFill). DOM에 없는 청크(비활성 branch
 *    슬롯)는 nodeHeight 0.
 *  - chrome: 단위 wrapper − Σ청크 실측(px, 조각별 최댓값·0 하한) — branch 래퍼·template padding 등
 *    청크 밖 잔여. 낱장마다 한 번 충전된다. 과충전(프룬된 래퍼·CSS gap)은 under-fill 방향이라
 *    넘치지 않는다(분할 여부 판단에도 안 쓰여 진동 없음 — contHeight 규약과 동형).
 */
export interface MeasuredUnitChunks {
  chrome: number;
  chunks: MeasuredNode[];
}

/**
 * 한 노드의 측정 결과. table이 있으면 표(행 분할 대상), repeat이 있으면 반복(항목 분할 대상),
 * 없으면 nodeHeight로 통짜 배치.
 */
export interface MeasuredNode {
  id: string;
  table?: MeasuredTable;
  /** repeat 측정 결과(항목 분할 대상). 높이는 unitHeights+gap 합산으로 계산(nodeHeight 불필요). */
  repeat?: MeasuredRepeat;
  /** 비표 노드 높이(table 없을 때). */
  nodeHeight?: number;
  /** 이 노드가 페이지 채움 노드면 아래 fillHeight를 계획이 계산. */
  pageFill?: boolean;
  /**
   * ComposerNodeBase.pageBreak === 'before' — 이 노드 앞에서 **무조건** 페이지를 나눈다
   * (구 SMS forceNewPage 동형, 모든 노드 공통). 표/repeat의 startOnNewPage(통째로 안 들어갈 때만
   * 조건부)와 의미가 다르다. 높이 0(빈 branch/0행 repeat 등) 노드는 무시(무의미한 빈 분할 방지).
   */
  breakBefore?: boolean;
  /** 채움 모드 — 'keep-below'(기본/미지정): 다음 형제 1개를 같은 페이지 바닥에 유지(여럿은 group으로 묶음).
   *  'fill-all': 흡수 없이 남은 공간 전부 채움(뒤 형제는 다음 페이지부터). */
  pageFillMode?: 'keep-below' | 'fill-all';
  /** 세로 채움 분배 그룹 키(NodeLayout.fillSplitGroup) — 같은 키의 인접 노드가 페이지 예산을 분배. */
  fillSplitGroup?: string;
  /** 세로 채움 분배 grow 비율(NodeLayout.grow, 미지정 1). */
  fillGrow?: number;
}

/**
 * 측정된 노드들을 A4 낱장(pageContentH)으로 배치한다(순수).
 *  - 비표 노드: 남은 공간에 안 들어가면 새 페이지에 통짜 배치(노드 자체는 분할 안 함).
 *  - 표: 남은 공간에 통째로 안 들어가면 **행 분할**. `safe[e]=true` 경계에서만 컷(safe엔 keep-together
 *    그룹 관통 경계만 false → keep-together 그룹은 통째 유지, 나머지는 자유 컷).
 *  - `startOnNewPage` ON + 이미 뭔가 쌓임: 표를 새 페이지에서 시작(그 후에도 페이지보다 크면 행 분할).
 *  - 오버사이즈(새 페이지인데도 한 안전 단위가 페이지보다 큼): safe 무시 강제 raw-split(최소 1행 전진).
 *  - 채움 노드(`pageFill` 비표): 자연 높이 + 남은 slack을 흡수해 예약 바닥 블록(다음 형제 1개)을
 *    페이지 바닥에 flush. 채움노드+바닥 블록이 남은 공간에 안 들어가면 keep-together로 다음 페이지 재배치.
 *  - 채움 표(`pageFill` 표): 통째 배치 가능하면 잔여 slack을 `fillSlack`으로 전달(렌더가 데이터 행
 *    아래를 빈 괘선 행으로 페인팅). 오버사이즈면 채움 생략(degrade) 후 기존 행 분할로 진행.
 *  - **반복 단위 안 채움 표**(`MeasuredRepeat.fillTable` — 단위 template의 유일한 pageFill 표,
 *    결정 2026-09-03): 채움 표가 **통짜로 렌더되는 낱장**의 잔여를 그 표에 `fillSlack`(인스턴스
 *    경로 키 `{단위경로}/{표id}`)으로 부여해 낱장을 소진한다 — 뒤 단위·뒤 노드는 자연히 다음
 *    낱장부터. 부여 지점 셋: ⓐ 단위가 통짜로 놓인 낱장(채움 단위가 슬라이스를 끝낸다 — 반복이
 *    통째로 들어가도 단위 배치로 라우팅) ⓑ 단위가 안쪽 경계에서 분할되면 **lead가 그려지는 낱장**
 *    (채움 표는 lead 소속이라 그 낱장에 통짜 렌더 — 함께 실린 안쪽 항목들이 밀려 낱장 바닥에 붙는다)
 *    ⓒ 채움 표 자신이 경계(`unitInner.fill`)면 전 행이 한 조각으로 담긴 낱장. **행 분할된 조각에는
 *    부여하지 않는다**(degrade — 기존 분할 로직 그대로). 한 낱장에 부여 후보가 둘 이상이면(중첩
 *    채움 중첩 등 비정형) 전부 생략한다(둘 다 늘리면 낱장이 넘친다).
 *  - repeat: 남은 공간에 통째로 안 들어가면 **항목 분할**(표 행 분할과 대칭). 분할 단위는 다단이면
 *    행(columns개 묶음 — 같은 행 항목이 함께 이동), 아니면 항목 1개. 항목은 통째 유지(중간 자르기
 *    금지). `startOnNewPage`는 표와 동형.
 *  - **조건부 재귀 분할(결정 2026-08-18)**: 한 단위가 **빈 페이지에도** 안 들어가고 그 단위에 안쪽
 *    분할 경계(`MeasuredRepeat.unitInner` = 안쪽 반복 항목)가 있으면, 한 단계 안으로 내려가 자를 수
 *    있는 경계까지 채우고 나머지를 다음 장으로 넘긴다(깊이 무관 — 안쪽도 unitInner를 가지면 또
 *    내려간다). 들어가면 통짜(현행 그대로), 경계가 없으면 단독 배치 + 넘침(현행 그대로).
 *    고정분(사진 등)이 커서 안쪽 항목을 하나도 못 얹으면 **고정분만** 배치하고 안쪽 항목은 다음
 *    낱장부터다(넘치며 밀어 넣지 않는다 — `'lead-only'`).
 *    어느 쪽이든 `forcedPlacement` 사실 신호가 붙는다. 저작 옵션은 하나도 늘지 않는다.
 *    쪼개진 항목의 lead(사진·설명 등)는 **첫 조각에만** 나온다(`suppressLead` — 뒷 조각은 남은
 *    안쪽 항목만 잇는다). 그래서 조각 높이 = (첫 조각이면 lead, 아니면 C) + 그 조각의 단위 합이다.
 *  - **순차 다경계(결정 2026-09-03 심야)**: 경계 후보가 여럿이라 유일 경계가 없는 단위
 *    (`MeasuredRepeat.unitChunks`)는 빈 낱장에도 안 들어갈 때 **흐름 청크열**(구간|경계, 문서
 *    순서 — `unitFlowChunks`)로 최상위 body 동형 순차 배치한다(placeChunks): 구간은 통짜, 경계는
 *    행/항목 분할, 채움 표 청크는 최상위 채움 표 동형(keep-below 흡수·행 분할 시 채움 생략).
 *    낱장별 가시 창은 `chunkRange` 채널로 나가고, 렌더는 그 창의 청크만 남긴다(pruneToChunkWindow).
 *    청크 분할은 구조적 흐름이라 forcedPlacement를 달지 않는다(경계 내부의 더 깊은 하강 신호는
 *    그대로). 유일 경계 형상(unitInner)은 종전 lead/continuation 모델 그대로다(회귀 0).
 *    **breakBefore 선언 청크를 가진 반복의 단위는 낱장에 통째로 들어가도 청크 경로로 배치한다**
 *    (2026-09-05) — 청크 안 pageBreak:'before'는 최상위와 같은 무조건 분할이라 "안 들어갈 때만"
 *    으로는 선언 의미가 죽는다. break 앞 청크는 현재 낱장 흐름을 잇고 break 청크에서 나뉜다.
 *    라우팅 근거는 `MeasuredRepeat.breakChunkDecl`(template 파생 — 전 단위 동일)이고 측정 높이는
 *    보지 않는다(유닛별 갈림·고착 방지 — 그 필드 주석). 실발동은 placeChunks h>0 게이트가 거른다.
 *    선언 없는 반복은 종전 그대로(회귀 0).
 * start 인덱스/행이 매 반복 단조 증가하므로 무한루프 없음.
 */
export function planEditPages(
  nodes: MeasuredNode[],
  pageContentH: number,
  EPS = 1,
): PagedItem[][] {
  const plan: PagedItem[][] = [[]];
  let page = 0;
  let used = 0;
  /**
   * 다음에 조각을 놓는 층이 높이에 더해야 할 **단위 사이 gap**(px). 재귀 하강은 newPage를 하지
   * 않으므로 다음 단위가 같은 낱장에 이어지는데, 그 gap이 필요한 자리는 **하강한 층**이라 각 층의
   * 지역 상태로는 못 잡는다. 낱장이 바뀌면 무효(newPage가 0으로 되돌린다).
   */
  let pendingJoinGap = 0;
  const newPage = () => {
    page += 1;
    plan[page] = [];
    used = 0;
    pendingJoinGap = 0;
  };
  // repeat 총높이 — 식은 `innerAxisTotalH` 하나만 둔다(사본을 두면 그 함수의 "한 곳에 둔다"는
  // 선언이 곧 거짓이 되고, 규칙이 바뀔 때 두 곳을 같이 고쳐야 한다).
  const repeatTotalH = (rm: MeasuredRepeat): number => innerAxisTotalH({ repeat: rm });
  // 노드의 통짜 높이(표=tableHeight, repeat=단위합+gap, 비표=nodeHeight). undefined 노드는 0.
  const nodeH = (m: MeasuredNode | undefined): number =>
    (m?.table ? m.table.tableHeight : m?.repeat ? repeatTotalH(m.repeat) : m?.nodeHeight) ?? 0;
  // startOnNewPage(표/repeat 조건부)·breakBefore(공통 무조건) 노드는 채움 노드의 바닥 블록으로
  // 흡수하지 않는다(자체 페이지 규칙 우선).
  const isStartOnNewPage = (m: MeasuredNode | undefined): boolean =>
    m?.table?.startOnNewPage === true ||
    m?.repeat?.startOnNewPage === true ||
    m?.breakBefore === true;
  // breakBefore 라우팅 신호(2026-09-05) — **template 파생 선언(breakChunkDecl)만** 본다. 이런
  // 단위는 낱장에 통째로 들어가도 통짜 배치가 성립하지 않는다(청크 안 pageBreak:'before'는
  // "안 들어갈 때만"의 조건부가 아니라 최상위와 같은 **무조건** 분할이다). 측정된 청크 높이로
  // 게이트하지 않는다 — 유닛별로 달라져 라우팅이 갈린 채 고착된다(MeasuredRepeat.breakChunkDecl
  // 주석). 안쪽 repeat 청크의 선언은 재귀로 위로 전파한다(깊이 무관 — 바깥이 통짜로 삼키면
  // 안쪽 선언이 영영 소비되지 않는다). 신호 없는 단위/반복은 종전 경로 그대로(회귀 0).
  // 안쪽은 **두 축** 다 걷는다(2026-09-14 C5): 순차 다경계(unitChunks의 repeat 청크)뿐 아니라
  // **유일 경계(unitInner의 repeat 축)** 도 — 바깥이 유일 경계 형상이면 안쪽 repeat은 unitChunks가
  // 아니라 unitInner에 매달리므로, 한 축만 걷으면 그 형상의 안쪽 선언이 바깥에 안 올라와 바깥이
  // 낱장에 통짜로 삼켜진다(repeatMultiBoundary '유일 경계 바깥 × 청크 break 안쪽' suite).
  const repeatHasBreakChunk = (r: MeasuredRepeat): boolean =>
    r.breakChunkDecl === true ||
    (r.unitChunks?.some(
      (uc) =>
        uc !== undefined &&
        uc.chunks.some((c) => c.repeat !== undefined && repeatHasBreakChunk(c.repeat)),
    ) ??
      false) ||
    (r.unitInner?.some((i) => i?.repeat !== undefined && repeatHasBreakChunk(i.repeat)) ?? false);
  // **단위 배치 라우팅 신호** 보유 반복(2026-09-14 C6) — 채움 표를 품은 단위(fillTable) 또는 break
  // 선언 보유(repeatHasBreakChunk). 아래 최상위 repeat 분기가 이 두 신호로 "통째로 들어가도 단위
  // 배치"를 결정하므로, 같은 술어를 한 곳에 둔다(두 자리가 갈리면 한쪽만 신호를 보게 된다).
  const isUnitRoutedRepeat = (m: MeasuredNode | undefined): boolean =>
    m?.repeat !== undefined &&
    (m.repeat.fillTable !== undefined || repeatHasBreakChunk(m.repeat));
  // 채움 노드의 keep-below 바닥 블록으로 **흡수해도 되는** 후보인가 — 자체 페이지 규칙
  // (isStartOnNewPage) 외에 단위 배치 라우팅 신호 보유 반복도 흡수하지 않는다(2026-09-14 C6).
  // 흡수하면 `{nodeId}` 통짜로 실려 그 라우팅(채움 단위의 낱장 소진·청크 break)을 통째로 건너뛴다
  // — 단독일 땐 여러 낱장이던 반복이 앞 채움 노드 하나 때문에 1낱장 통짜가 된다. fillSplit 예약
  // 루프의 `c.repeat != null` 중단 규약과 동형이고, 네 흡수 자리(채움 표 통짜 keep-below·채움 비표
  // 노드·채움 표 마지막 세그먼트·청크 채움 표)가 이 하나를 쓴다.
  const isKeepBelowAbsorbable = (m: MeasuredNode | undefined): boolean =>
    m !== undefined && !isStartOnNewPage(m) && !isUnitRoutedRepeat(m);

  // ── 조건부 재귀 분할 (P6-1, 결정 2026-08-18) ────────────────────────────
  // 규칙은 하나다: **남은 공간에 들어가면 통짜(현행 그대로), 안 들어갈 때만 한 단계 안으로.**
  // 저작 축은 하나도 늘지 않는다 — 경계는 구조(안쪽 repeat 항목)가 이미 제공한다.

  /** 항목 경로 → 그 항목의 lead가 **처음 렌더된 낱장 번호**. 그 낱장에서만 lead를 그린다. */
  const leadFirstPage = new Map<string, number>();
  /**
   * 항목 경로 → 그 항목의 고정분을 **마지막으로 used에 반영한 낱장 번호**.
   * 한 낱장에 같은 항목의 조각이 두 번 놓일 수 있어(3단 중첩에서 안쪽 층이 한 낱장에 끝나고 그
   * 위 층의 다음 단위가 이어질 때) 고정분을 두 번 더하면 used가 부풀어 낱장이 일찍 끊긴다.
   */
  const chromeChargedPage = new Map<string, number>();

  /**
   * 항목 범위 지시를 현재 낱장에 push하되 **같은 조회 키의 기존 지시와는 하나로 합친다**.
   *
   * buildPageChannels가 itemRanges를 조회 키(pagedItemKey) 하나로 만들므로, 한 낱장에 같은 키가
   * 둘이면 뒤가 앞을 덮어 앞 조각이 조용히 사라진다. 재귀 분할에서는 **조상 지시가 조각마다 다시
   * 나오고**(같은 범위 반복) 쪼갠 단위 뒤에 다음 단위가 같은 낱장에 이어지므로(인접 범위) 두 경우
   * 모두 실제로 생긴다. 범위는 항상 인접·오름차순이라 합치기가 안전하다.
   *
   * 기존(비중첩) 경로는 슬라이스마다 newPage라 한 낱장에 같은 키가 둘이 될 수 없어 이 합치기가
   * 발동하지 않는다 → 계획이 종전과 동일(회귀 0).
   * 신호가 붙는/붙은 지시도 합친다(더 강한 신호 유지 — 2026-09-14 W1, 본문 주석) — 키당 entry는
   * 낱장에 **정확히 하나**다.
   */
  const pushRangeEntry = (
    nodeId: string,
    path: string | undefined,
    range: [number, number],
    channel: UnitChannel,
    forcedPlacement?: ForcedPlacement,
    suppressLead?: true,
  ) => {
    const key = pagedItemKey({ nodeId, path });
    let prev: PagedItem | undefined;
    for (let k = plan[page].length - 1; k >= 0; k -= 1) {
      if (pagedItemKey(plan[page][k]) === key) {
        prev = plan[page][k];
        break;
      }
    }
    // 같은 노드는 같은 채널로만 나온다(축은 측정이 정하고 낱장 중간에 바뀌지 않는다).
    const prevRange =
      channel === 'row' ? prev?.rowRange : channel === 'item' ? prev?.itemRange : prev?.chunkRange;
    if (prev !== undefined && prevRange !== undefined) {
      // 같은 키의 **두 번째 entry는 어떤 경우에도 push하지 않는다** — 채널(buildPageChannels)은
      // 키당 단일 슬롯이라 마지막 entry가 앞을 덮어 그 낱장의 **앞 조각 렌더가 통째로 사라진다**
      // (2026-09-05 실사고: 세션 1·2의 TBM 내용~특이 소실 — red 재현은 repeatMultiBoundary '실전
      // 형상' suite). 같은 낱장·같은 키의 범위는 항상 인접·오름차순이다(조상 재방출 = 같은 범위,
      // 하강의 조상 재방출 = 이미 병합된 prev의 부분집합, 이 층의 다음 단위 = 인접 확장)라 **합집합
      // 하나**로 안전하다 — 동일·포함·인접 세 경우가 전부 이 식 하나에 들어간다.
      //
      // 강제 신호는 **더 강한 쪽을 남긴다**(2026-09-14 W1). 종전엔 "신호가 붙는/붙은 지시는 합치지
      // 않는다"(어느 조각의 사실인지 흐려진다)였는데, 그 예외가 곧 두 번째 entry의 입구였다 — 3단
      // 중첩에서 하강한 층의 조상 재방출(신호 없음 [0,1)) 뒤에 같은 층의 다음 단위 슬라이스
      // ('split' [1,2))가 같은 낱장에 이어지면(하강은 newPage를 하지 않는다) 병합이 거부돼 두 번째
      // entry가 push되고, 안쪽 조각의 부모 범위가 [1,2)로 덮여 그 낱장의 안쪽 단위가 사라졌다
      // (pagePlanNestedSplit '3단 중첩 — 같은 키 entry 1개' spec). 신호는 표시 전용 사실이라
      // 조각이 사라지는 것과 견줄 일이 아니다.
      const merged: [number, number] = [
        Math.min(prevRange[0], range[0]),
        Math.max(prevRange[1], range[1]),
      ];
      if (merged[0] !== prevRange[0] || merged[1] !== prevRange[1]) {
        if (channel === 'row') prev.rowRange = merged;
        else if (channel === 'item') prev.itemRange = merged;
        else prev.chunkRange = merged;
      }
      const stronger = strongerForced(prev.forcedPlacement, forcedPlacement);
      if (stronger !== undefined && stronger !== prev.forcedPlacement) {
        prev.forcedPlacement = stronger;
      }
      return;
    }
    const span: [number, number] = [range[0], range[1]];
    const base: PagedItem = path === undefined ? { nodeId } : { path, nodeId };
    if (channel === 'row') base.rowRange = span;
    else if (channel === 'item') base.itemRange = span;
    else base.chunkRange = span;
    const item: PagedItem = base;
    if (forcedPlacement !== undefined) item.forcedPlacement = forcedPlacement;
    if (suppressLead === true) item.suppressLead = true;
    plan[page].push(item);
  };

  /**
   * 조각 지시가 나가는 렌더 채널 — repeat 항목이면 `itemRange`, 표 행이면 `rowRange`,
   * 순차 다경계의 단위 청크 창이면 `chunkRange`(범위 병합 규약을 그대로 태워 한 낱장에
   * 인접 청크가 하나의 창으로 합쳐진다).
   */
  type UnitChannel = 'item' | 'row' | 'chunk';

  /**
   * 계획기가 쪼개는 **단위 축**(계획 내부 표현) — repeat의 항목열과 표의 행열이 배치 규칙을 통째로
   * 공유하게 만드는 어댑터다. 두 축의 차이는 셋뿐이고, 그 셋만 필드로 드러낸다:
   *   ① 단위 → 렌더 범위 변환(다단 repeat은 항목 인덱스로 확장, 표는 행 인덱스 그대로)
   *   ② 렌더 채널 이름(itemRange / rowRange)
   *   ③ 컷 허용 경계(`safe` — 표의 keep-together 그룹 관통 금지. repeat은 항상 자유)
   * 나머지(고정분 lead/C 회계, 후퇴, 재귀 하강, 낱장 이월 gap)는 축과 무관해 한 벌만 존재한다.
   */
  interface UnitAxis {
    unitHeights: number[];
    /** 단위 사이 간격(px). 표 행은 0(행 높이에 이미 포함). */
    gap: number;
    /**
     * 단위 u 앞에서 컷해도 되는가(길이 unitCount+1, computeSafeCutRows 산출).
     * **미지정 = 전부 자유**(repeat 축) — 이 경우 아래 배치 루프가 종전 코드와 완전히 같게 흐른다.
     */
    safe?: boolean[];
    toRange: (a: number, b: number) => [number, number];
    channel: UnitChannel;
    /** 단위 u의 안쪽 경계(더 내려갈 수 있으면). 표 축은 항상 undefined. */
    innerAt: (u: number) => MeasuredRepeatInner | undefined;
    /**
     * 단위 u의 흐름 청크 측정치(순차 다경계 — `MeasuredRepeat.unitChunks`). unitInner와 상호
     * 배타(측정이 sole boundary null일 때만 세운다). 표 축은 항상 undefined.
     */
    chunksAt?: (u: number) => MeasuredUnitChunks | undefined;
    /**
     * 이 축의 단위들이 breakBefore **선언**으로 청크 라우팅 대상(repeat 축 전용 —
     * repeatHasBreakChunk, template 파생이라 전 단위 동일). 표 축은 항상 undefined.
     */
    breakRouted?: true;
    /**
     * 이 축 단위 template의 채움 표 id(`MeasuredRepeat.fillTable`) — 통짜로 놓인 단위가 낱장을
     * 소진하고 잔여를 이 표에 부여한다(repeat 축 전용, 결정 2026-09-03).
     */
    fillTable?: string;
    /**
     * 이 축 **자신이 채움 표**(경계 겸임 — `MeasuredRepeatInner.fill`, 표 축 전용). 전 행이 한
     * 조각으로 담긴 낱장에서만 잔여를 부여한다(행 분할 조각은 degrade).
     */
    fillSelf?: true;
  }

  const repeatAxis = (rm: MeasuredRepeat): UnitAxis => ({
    unitHeights: rm.unitHeights,
    gap: rm.gap,
    // 다단이면 행×columns(마지막 행은 totalItems로 클램프).
    toRange: (a, b) => [a * rm.columns, Math.min(b * rm.columns, rm.totalItems)],
    channel: 'item',
    innerAt: (u) => rm.unitInner?.[u],
    chunksAt: (u) => rm.unitChunks?.[u],
    breakRouted: repeatHasBreakChunk(rm) ? true : undefined,
    fillTable: rm.fillTable,
  });

  /**
   * 표를 단위 축으로 본다 — 단위 = 행, 높이 = rowTop 차분, gap = 0.
   * ⚠️ `startOnNewPage`는 읽지 않는다 — 항목 **안**에서 "새 장"은 의미가 모호하고, 여기서 표는
   * 분할 경계로만 쓰인다(안쪽 repeat의 startOnNewPage를 무시하는 규약과 동형).
   */
  const tableAxis = (tm: MeasuredTable, fillSelf?: true): UnitAxis => {
    const unitHeights: number[] = [];
    for (let r = 0; r < tm.totalRows; r += 1) unitHeights[r] = tm.rowTop(r + 1) - tm.rowTop(r);
    return {
      unitHeights,
      gap: 0,
      safe: tm.safe,
      toRange: (a, b) => [a, b],
      channel: 'row',
      innerAt: () => undefined,
      fillSelf,
    };
  };

  const axisOf = (inner: MeasuredRepeatInner): UnitAxis => {
    // 축 유니온 좁힘은 순수 유니온(MeasuredInnerAxis) 위에서 — 교차 타입(Base &) 위 truthiness
    // 좁힘에 기대지 않는다. fill은 Base 필드라 좁힘 전에 떼어 둔다.
    const ax: MeasuredInnerAxis = inner;
    return ax.repeat ? repeatAxis(ax.repeat) : tableAxis(ax.table, inner.fill);
  };

  /** 이 층까지 내려오며 지나온 조상 항목들의 고정분 — 낱장마다 lead(첫 조각) 또는 C(뒷 조각). */
  interface AncestorChrome {
    path: string;
    leadHeight: number;
    /** 미관측이면 undefined → 0으로 본다(분할 여부 판단에 안 쓰므로 진동 없음). */
    contHeight?: number;
    /**
     * 이 조상 항목의 lead에 속한 채움 표 id — lead가 그려지는 낱장의 잔여를 이 표에 부여한다
     * (부여 지점 ⓑ). 경계 겸임(표 자신이 경계)이면 여기 싣지 않고 `UnitAxis.fillSelf`가 담당.
     */
    fillTable?: string;
  }

  /**
   * 한 **단위 축**(repeat 인스턴스의 항목열 / 표 인스턴스의 행열)을 현재 페이지부터 배치한다
   * (**깊이 무관 재귀** — 2단 특수 분기 없음).
   *
   * 단위는 통째 유지하고(중간 자르기 금지 — 카드 판독성), 새 페이지에도 안 들어가는 단위는
   * **한 단계 안(안쪽 반복 항목 또는 안쪽 표의 행)** 으로 내려가 자를 수 있는 경계까지 채운다.
   * 내려간 층도 이 함수를 그대로 다시 쓴다 — 3단 이상 중첩에서도, 축이 항목이든 행이든 같은
   * 규칙이다(축의 차이는 `UnitAxis` 세 필드에만 있다).
   *
   * ⚠️ **C(continuation chrome)는 분할 *여부* 판단에 절대 쓰지 않는다.** 분할 결정은
   * (단위 통짜 높이 vs 빈 페이지)만 보고, C는 "그 조각에 몇 개가 들어가나"에만 관여한다.
   * C는 통짜 상태에서 관측 불가(undefined)라, 만약 분할 결정이 C에 의존하면 관측 전후로 결정이
   * 뒤집혀 **통짜↔분할 주기 진동**이 된다. 이 분리가 수렴의 근거다.
   *
   * ⚠️ 안쪽 repeat의 `startOnNewPage`는 **읽지 않는다** — 항목 내부에서 "새 장"은 의미가 모호하고,
   * 여기서 안쪽 반복은 분할 경계로만 쓰인다(결정 2026-08-18 열린 논점 2의 규약).
   */
  const placeUnits = (
    axis: UnitAxis,
    hostId: string,
    hostPath: string | undefined,
    ancestors: readonly {
      nodeId: string;
      path?: string;
      range: [number, number];
      channel: UnitChannel;
    }[],
    chromes: readonly AncestorChrome[],
    markSplit: boolean,
  ): void => {
    const unitCount = axis.unitHeights.length;
    // 이 낱장에서 실제로 그려지는 조상 고정분 합 — 처음 나오는 항목은 lead, 이어지는 조각은 C.
    const chromeH = (): number =>
      chromes.reduce((sum, c) => {
        // 이미 이 낱장에서 반영했으면 다시 더하지 않는다(위 chromeChargedPage 주석).
        if (chromeChargedPage.get(c.path) === page) return sum;
        const first = leadFirstPage.get(c.path);
        return sum + (first === undefined ? c.leadHeight : (c.contHeight ?? 0));
      }, 0);
    /**
     * **빈 낱장에 놓았다면** 얼마의 고정분이 붙는가 — "옮기면 들어가나?"라는 가정 판정 전용.
     * chromeH와 달리 chromeChargedPage(현재 낱장에서 이미 반영했는지)를 보지 않는다. 그걸 보면
     * 가정 질문의 답이 현재 낱장 상태에 오염된다.
     */
    const freshChromeH = (): number =>
      chromes.reduce((sum, c) => {
        const first = leadFirstPage.get(c.path);
        return sum + (first === undefined ? c.leadHeight : (c.contHeight ?? 0));
      }, 0);
    const toRange = axis.toRange;
    /**
     * 단위 u를 청크 경로로 라우팅할 때 그 청크열(2026-09-05) — 축의 breakBefore **선언**
     * (breakRouted, template 파생이라 전 단위 동일)이 근거다. 측정 높이는 보지 않는다 — 유닛별로
     * 달라져 라우팅이 갈린 채 고착된다(MeasuredRepeat.breakChunkDecl 주석). 유일 경계(inner)가
     * 있으면 종전 경로 우선(아래 uc 분기와 같은 방어 — 측정 규약상 상호 배타). 선언이 있는데
     * 청크 측정이 없으면(방어 — chunkMode가 선언을 포함하므로 정상 경로에선 없다) 종전 슬라이스로
     * degrade한다.
     */
    const breakChunksAt = (u: number): MeasuredUnitChunks | undefined => {
      if (axis.breakRouted !== true) return undefined;
      if (axis.innerAt(u) != null) return undefined;
      return axis.chunksAt?.(u);
    };
    /**
     * 단위 u의 **유일 경계가 break 선언 보유 repeat 축**이면 그 경계(2026-09-14 C5) — 위
     * breakChunksAt의 짝이다. 유일 경계(unitInner) 형상의 안쪽 repeat은 청크열이 아니라 inner에
     * 매달리므로, 그 안쪽의 선언은 **fit 여부와 무관하게 내려가야** 소비된다(placeChunks의
     * mustDescend와 같은 게이트 — 바깥이 통짜 슬라이스로 삼키면 안쪽 pageBreak:'before'가
     * "빈 낱장에도 안 들어갈 때만"의 조건부가 된다). 표 축 경계·선언 없는 repeat 경계는 종전
     * 경로 그대로(회귀 0).
     */
    const innerBreakAt = (u: number): MeasuredRepeatInner | undefined => {
      if (axis.breakRouted !== true) return undefined;
      const inner = axis.innerAt(u);
      return inner?.repeat !== undefined && repeatHasBreakChunk(inner.repeat) ? inner : undefined;
    };
    /** 단위 u가 break 선언 라우팅 대상인가(청크 경로 또는 안쪽 경계 하강) — 슬라이스에 넣지 않는다. */
    const isBreakRoutedUnit = (u: number): boolean =>
      breakChunksAt(u) !== undefined || innerBreakAt(u) !== undefined;
    /**
     * 단위 u를 그 유일 경계 `inner`로 **한 단계 내려가** 배치한다(현재 낱장의 남은 공간부터).
     * 두 호출부(안 들어가는 단위의 조건부 하강 / 안쪽 선언 보유 단위의 무조건 하강)가 조상·고정분
     * 조립을 공유한다 — 따로 두면 채움 표 부여(ⓑ) 같은 규약이 한쪽에서만 바뀐다.
     */
    const descendInner = (inner: MeasuredRepeatInner, u: number, markSplitBelow: boolean): void => {
      placeUnits(
        axisOf(inner),
        inner.nodeId,
        inner.path,
        [
          ...ancestors,
          {
            nodeId: hostId,
            path: hostPath,
            range: toRange(u, u + 1),
            channel: axis.channel,
          },
        ],
        // 내려간 층에서 이 단위의 lead는 첫 조각에만, 뒷 조각엔 C만 남는다.
        [
          ...chromes,
          {
            path: inner.path,
            leadHeight: Math.max(0, inner.leadHeight),
            contHeight: inner.contHeight,
            // 이 단위의 채움 표가 lead 소속이면(경계와 별개 노드) lead가 그려지는 낱장에서
            // 잔여를 부여한다(부여 지점 ⓑ). 경계 겸임이면 fillSelf(ⓒ)가 담당 — 중복 부여 방지.
            fillTable:
              axis.fillTable !== undefined && axis.fillTable !== inner.nodeId
                ? axis.fillTable
                : undefined,
          },
        ],
        markSplitBelow,
      );
    };
    let startUnit = 0;
    while (startUnit < unitCount) {
      // breakBefore 청크 보유 단위는 **fit 여부와 무관하게** 청크 경로로 라우팅한다(2026-09-05) —
      // 아래 채움 루프가 이 단위를 통짜로 삼키면 청크 안 pageBreak:'before'가 "빈 낱장에도 안
      // 들어갈 때만" 동작하는 조건부가 된다(선언 의미는 무조건이다). placeChunks가 break 앞
      // 청크는 현재 낱장 흐름에 잇고 break 청크에서 newPage한다(최상위 breakBefore 동형).
      const ucb = breakChunksAt(startUnit);
      if (ucb !== undefined) {
        placeChunks(
          ucb,
          hostId,
          hostPath,
          toRange(startUnit, startUnit + 1),
          axis.channel,
          `${hostPath !== undefined ? `${hostPath}/` : ''}${hostId}::${startUnit}`,
          ancestors,
          chromes,
        );
        pendingJoinGap = axis.gap;
        startUnit += 1;
        continue;
      }
      // 유일 경계 안쪽 repeat이 break 선언을 품은 단위도 **fit 여부와 무관하게** 내려간다(2026-09-14
      // C5, innerBreakAt 주석) — 내려간 층의 단위 라우팅이 청크 경로로 보내 안쪽 선언이 소비된다.
      // 하강 규약은 아래 조건부 하강과 같다(newPage 없이 현재 낱장 남은 공간부터, 다음 단위는
      // 마지막 조각 아래로). 강제 신호는 이 층의 것을 그대로 물린다(구조적 흐름 — placeChunks 동형).
      const innerB = innerBreakAt(startUnit);
      if (innerB !== undefined) {
        descendInner(innerB, startUnit, markSplit);
        pendingJoinGap = axis.gap;
        startUnit += 1;
        continue;
      }
      const avail = pageContentH - used;
      // 이 낱장 기준으로 다시 계산한다 — newPage 후에는 lead가 이미 앞 낱장에서 나갔을 수 있다.
      const chrome = chromeH();
      // 같은 낱장에 이 반복의 앞 단위가 이미 놓였으면 **단위 사이 gap이 하나 더** 붙는다.
      // 채움 루프의 `u > startUnit ? rm.gap : 0`은 한 조각 **안**의 gap만 세므로 이 자리를 못 잡는다.
      // 생기는 경로는 재귀 하강 뒤다 — 하강은 newPage를 하지 않아(앞 장 하단을 비우지 않으려는
      // 규약) 다음 단위가 같은 낱장에 이어지는데, 그때 두 항목 사이 gap이 빠졌다(SI 템플릿
      // si-cur-repeat gap=12 → 그 낱장이 12px 넘칠 수 있었다).
      // ⚠️ gap이 필요한 자리는 **하강한 층**이라 이 층의 지역 상태로는 못 잡는다 — 계획 전역
      // pendingJoinGap으로 넘겨 다음에 조각을 놓는 층이 소비한다(newPage가 무효화).
      const joinGap = pendingJoinGap;
      // startUnit부터 avail 안에 들어가는 최대 endUnit(고정분 + 단위 사이 gap 포함, 단위 통째 유지).
      // 표 축은 여기에 안전 경계(axis.safe) 제약이 하나 더 붙는다 — keep-together 그룹을 관통하는
      // 경계에서는 끝낼 수 없다. rawEnd/rawH는 그 제약을 **무시했을 때**의 끝으로, 안전 경계가
      // 하나도 안 잡히는 오버사이즈 그룹에서만 강제 컷으로 쓰인다(최상위 표 분할과 같은 규약).
      // safe 미지정(repeat 축)이면 endUnit === rawEnd·sliceH === rawH라 종전 흐름과 동일하다.
      let endUnit = startUnit;
      let sliceH = chrome + joinGap;
      let rawEnd = startUnit;
      let rawH = sliceH;
      /** 채움 단위가 이 슬라이스를 끝냈다(부여 지점 ⓐ) — 잔여를 그 단위의 채움 표에 부여. */
      let fillUnitPlaced = false;
      for (let u = startUnit; u < unitCount; u += 1) {
        // breakBefore 선언 라우팅 단위(청크 경로·안쪽 경계 하강)는 이 슬라이스에 넣지 않는다 —
        // 다음 회차의 startUnit이 되어 위 라우팅을 탄다(u === startUnit은 위 라우팅이 이미 걸러
        // 여기 안 온다).
        if (u > startUnit && isBreakRoutedUnit(u)) break;
        const add = (u > startUnit ? axis.gap : 0) + axis.unitHeights[u];
        if (rawH + add > avail + EPS) break;
        rawH += add;
        rawEnd = u + 1;
        if (axis.safe === undefined || u + 1 === unitCount || axis.safe[u + 1]) {
          endUnit = u + 1;
          sliceH = rawH;
        }
        // 채움 단위(template에 채움 표 보유)가 통짜로 들어갔다 → 이 단위가 낱장을 소진한다.
        // 슬라이스를 여기서 끝내 뒤 단위는 다음 낱장부터(채움 표 뒤로 빈 괘선 행이 바닥까지 차므로
        // 뒤 단위를 같은 낱장에 이어 붙일 자리가 없다). safe는 repeat 축에 없어(미지정 = 전부 자유)
        // endUnit === u+1이 항상 성립하고, 표 축은 fillTable이 없어 이 분기에 오지 않는다.
        if (axis.fillTable !== undefined && endUnit === u + 1) {
          fillUnitPlaced = true;
          break;
        }
      }
      let forced: ForcedPlacement | undefined = markSplit ? 'split' : undefined;
      if (endUnit <= startUnit) {
        // 한 단위도 못 넣었다. 여기서 "빈 낱장으로 옮길까, 남은 공간을 쓸까"를 가른다.
        //
        // 판정의 축은 **이 단위가 빈 낱장에는 통째로 들어가는가**다. 들어간다면 지금 못 넣은 건
        // 순전히 앞 노드 때문이므로 옮겨서 통짜로 놓는다("들어가면 통짜" — 회귀 0). 빈 낱장에도
        // 안 들어간다면 어차피 쪼개거나 넘칠 운명이라, 남은 공간을 버릴 이유가 없다
        // (2026-08-18 dev 실측: 제목만 있고 988px가 빈 낱장이 생겼다 — 사진 묶음이 빈 낱장에도
        // 안 들어가는데도 옮겨졌다).
        //
        // ⚠️ 판정에 쓰는 고정분은 **빈 낱장에 놓았을 때의 값**이다(freshChromeH) — 현재 낱장에서
        // 이미 반영했는지(chromeChargedPage)와 무관해야 "옮기면 들어가나?"라는 가정 질문의 답이
        // 된다. 입력이 전부 측정값(unitHeights·lead·C)과 계획 지역 상태뿐이라 렌더 결과에
        // 의존하지 않는다 → 같은 측정이면 같은 계획, 즉 수렴이 깨지지 않는다.
        const inner = axis.innerAt(startUnit);
        // 순차 다경계(unitChunks) — unitInner와 상호 배타(측정 규약)지만 방어적으로 inner 우선.
        const uc = inner == null ? axis.chunksAt?.(startUnit) : undefined;
        const fitsOnEmptyPage =
          freshChromeH() + axis.unitHeights[startUnit] <= pageContentH + EPS;
        // 남은 공간을 **실제로 쓸 수 있는** 수단이 있어야 머문다: 여기서 쪼개거나(inner) 고정분만
        // 놓거나(canLeadOnly). 둘 다 없으면(자를 경계 없는 오버사이즈 단위) 머물러 봐야 넘침만
        // 커지므로 종전대로 단독 낱장으로 옮긴다.
        const drawsLead = hostPath !== undefined && leadFirstPage.get(hostPath) === undefined;
        const contChromeH = chromes.reduce((s, c) => s + (c.contHeight ?? 0), 0);
        const postponedFits = contChromeH + axis.unitHeights[startUnit] <= pageContentH + EPS;
        // 고정분이 **이 낱장에** 들어가야 후퇴가 성립한다 — 앞 단위와의 gap(joinGap)까지 포함해서.
        const canLeadOnly =
          drawsLead && postponedFits && chrome > 0 && chrome + joinGap <= avail + EPS;
        if (used > 0 && (fitsOnEmptyPage || (inner == null && uc === undefined && !canLeadOnly))) {
          newPage();
          continue;
        }
        if (rawEnd > startUnit) {
          // **표 축 전용 경로**(repeat은 safe가 없어 rawEnd === endUnit === startUnit이라 여기 안 온다).
          // 단위 자체는 들어가는데 안전 경계가 하나도 안 잡힌다 = keep-together 그룹이 낱장보다 크다.
          // 위 `used > 0` 분기가 이미 빈 낱장으로 옮겨 본 뒤이므로 그 그룹은 어차피 못 지킨다 →
          // 최상위 표 분할의 "safe 무시 강제 raw-split"과 같은 규약으로 자른다.
          // 넘치는 배치가 아니므로 forced는 그대로 둔다('split' 또는 없음).
          endUnit = rawEnd;
          sliceH = rawH;
        } else if (inner) {
          // 안쪽 경계가 있으면 **한 단계 내려간다**(현재 낱장의 남은 공간부터).
          // ⚠️ "내려가도 첫 조각이 들어맞아야 내려간다"는 사전 게이트를 두지 않는다 — 1차 시도가
          // 그 게이트 때문에 첫 조각이 13px 모자란 항목을 통짜로 두어 663px 넘겼다. 내려가면
          // 최악이라도 통짜와 같고(조각 1개) 대개는 훨씬 덜 넘친다.
          descendInner(inner, startUnit, true);
          // 이 층의 **다음** 단위가 같은 낱장에 이어지면 이 층의 gap만큼 떨어진다.
          // (하강 직전에 넘긴 joinGap은 내려간 층이 이미 소비했거나 newPage로 무효화됐다.)
          pendingJoinGap = axis.gap;
          startUnit += 1;
          // 쪼갠 단위의 **마지막 조각은 페이지를 다 채우지 않았을 수 있다** — 다음 단위가 그 아래로
          // 이어지도록 여기서 newPage를 하지 않는다(앞 장 하단이 크게 비는 것이 이 개편의 발단이다).
          continue;
        } else if (uc !== undefined) {
          // 순차 다경계(결정 2026-09-03 심야): 유일 경계가 없는 단위를 흐름 청크열로 배치한다 —
          // 최상위 body 동형(구간 통짜 / 경계 항목·행 분할 / 채움 표 keep-below). 하강 규약은
          // inner와 동형: newPage 없이 현재 낱장 남은 공간부터, 다음 단위는 마지막 조각 아래로.
          placeChunks(
            uc,
            hostId,
            hostPath,
            toRange(startUnit, startUnit + 1),
            axis.channel,
            `${hostPath !== undefined ? `${hostPath}/` : ''}${hostId}::${startUnit}`,
            ancestors,
            chromes,
          );
          pendingJoinGap = axis.gap;
          startUnit += 1;
          continue;
        }
        // 자를 경계가 없다. 여기서 갈림길은 **고정분이 이 낱장에 들어가느냐**다.
        //
        // ⓐ 고정분은 들어가는데 안쪽 항목을 하나 얹으면 넘친다 → **고정분만** 배치하고 안쪽
        //    항목은 다음 낱장부터(`'lead-only'`). "최소 1단위 전진"은 무한루프를 막으려는 규칙이지
        //    넘침을 감수하라는 규칙이 아니고, 넘치는 배치는 결정 기록이 말한 '채움'이 아니다
        //    (2026-08-18 dev 실측: 사진+발견 1건이 135px 넘쳐 배치됐다 — 사용자 기대는 사진만).
        //    무한루프 없음: 이 후퇴는 그 항목의 **첫 낱장에서만** 성립하고(아래 drawsLead), 다음
        //    낱장에서는 고정분이 억제돼 chrome=C로 줄므로 반드시 1개 이상 전진한다.
        // ⓑ 고정분 자체가 페이지를 넘는다(사진 하나가 A4보다 큼) → 후퇴해도 소용없다. 종전대로
        //    최소 1단위와 함께 단독 배치 + 넘침. 넘침이 정당한 유일한 경우다.
        //
        // ⓒ 단, **후퇴가 실제로 도움이 될 때만** 후퇴한다(canLeadOnly의 postponedFits) — 미룬
        //    단위가 다음 낱장(고정분이 억제돼 chrome=C인 장)에도 안 들어갈 만큼 크면(단위 하나가
        //    A4 초과) 후퇴해 봐야 여전히 넘치고 거의 빈 낱장만 하나 더 생긴다. 그때는 종전대로
        //    함께 배치한다. 이 조건은 종료 증명도 강화한다: 미룬 단위가 다음 낱장에 들어감이
        //    보장되므로 그 낱장은 반드시 전진한다.
        else if (canLeadOnly) {
          endUnit = startUnit; // 안쪽 0건 — 렌더는 빈 범위라 항목을 하나도 그리지 않는다
          sliceH = chrome + joinGap;
          forced = 'lead-only';
        } else {
          endUnit = startUnit + 1;
          sliceH = chrome + joinGap + axis.unitHeights[startUnit];
          // ⚠️ 'split'을 덮어쓴다 — 쪼개고도 넘쳤다는 게 더 심한 사실이고, P6-2가 그걸 보고
          // "구조를 고치라"고 말해야 한다(쪼갠 사실만 알리면 저작자가 손댈 이유를 못 찾는다).
          forced = 'overflow';
        }
      }
      // (여기서 endUnit > startUnit이면 sliceH ≤ avail + EPS가 위 채움 루프로 보장된다 —
      //  넘침은 전부 위 분기에서만 나온다.)
      // 조각 continuation 판정 — 이 낱장에 **처음 나오는** 항목만 lead를 그린다.
      // ⚠️ hostPath만 보면 안 된다. 3단 이상 중첩에서는 바깥 항목도 이 낱장에 조각으로 이어지므로,
      // 지나온 조상 항목(chromes) 전부를 판정해야 바깥 lead가 매 낱장에 되풀이되지 않는다
      // (chromeH가 이미 조상별로 lead/C를 가려 쓰므로, 표식도 같은 축으로 내야 계획↔렌더가 맞다).
      // 이 슬라이스가 낱장 잔여를 부여할 채움 표 후보(결정 2026-09-03) — 정확히 하나일 때만 부여
      // (둘 이상이면 둘 다 늘려 낱장이 넘치므로 전부 생략 = degrade).
      const fillGrants: { path: string; nodeId: string }[] = [];
      const suppressedPaths = new Set<string>();
      for (const c of chromes) {
        const first = leadFirstPage.get(c.path);
        if (first === undefined) {
          leadFirstPage.set(c.path, page);
          // 부여 지점 ⓑ — 이 항목의 lead(채움 표 포함)가 이 낱장에 그려진다.
          if (c.fillTable !== undefined) fillGrants.push({ path: c.path, nodeId: c.fillTable });
        } else if (first !== page) suppressedPaths.add(c.path);
        // 이 낱장에서 고정분을 반영했다고 기록 — 같은 낱장에 이 항목의 조각이 또 놓여도 중복 가산 금지.
        chromeChargedPage.set(c.path, page);
      }
      // 부여 지점 ⓐ — 채움 단위가 통짜로 이 슬라이스를 끝냈다(슬라이스 = 그 단위 하나).
      if (fillUnitPlaced && axis.fillTable !== undefined) {
        fillGrants.push({
          path: `${hostPath !== undefined ? `${hostPath}/` : ''}${hostId}::${startUnit}`,
          nodeId: axis.fillTable,
        });
      }
      // 부여 지점 ⓒ — 채움 표 자신이 경계이고 전 행이 한 조각으로 담겼다(행 분할이면 부여 없음).
      if (axis.fillSelf === true && hostPath !== undefined && startUnit === 0 && endUnit === unitCount) {
        fillGrants.push({ path: hostPath, nodeId: hostId });
      }
      const supp = (p?: string): true | undefined =>
        p !== undefined && suppressedPaths.has(p) ? true : undefined;
      // 조상 지시는 조각마다 다시 나와야 한다 — 렌더가 부모 서브트리를 타고 내려가며 안쪽 지시를
      // 소비하므로 조상이 같은 낱장에 함께 있어야 한다. 조상 항목의 lead 억제도 여기 실린다
      // (그 항목을 그리는 RepeatItem이 자기 경로로 조회하므로 경로별로 따로 나가야 한다).
      for (const a of ancestors) {
        pushRangeEntry(a.nodeId, a.path, a.range, a.channel, undefined, supp(a.path));
      }
      pushRangeEntry(
        hostId,
        hostPath,
        toRange(startUnit, endUnit),
        axis.channel,
        forced,
        supp(hostPath),
      );
      // 채움 부여: 슬라이스 뒤 잔여를 채움 표에 fillSlack으로 부여하고 낱장을 소진한다(최상위
      // pageFill 표와 같은 Math.round 양자화 — pagePlansEqual 정확비교의 float 노이즈 차단).
      // 측정 진동 없음: 측정이 채움 행(data-composer-fill-row) 높이를 자연높이에서 차감하므로
      // 확장분이 되먹지 않고, slack 입력(avail·sliceH)이 전부 자연 측정값이라 같은 측정이면 같은
      // 계획이다. 후보 둘 이상이면 전부 생략(위 fillGrants 주석).
      if (fillGrants.length === 1) {
        const slack = Math.round(avail - sliceH);
        if (slack > 0) {
          plan[page].push({ path: fillGrants[0].path, nodeId: fillGrants[0].nodeId, fillSlack: slack });
          used += slack;
        }
      }
      // 이 조각이 joinGap을 소비했다 — 다음 조각이 같은 낱장에 오면 이 층의 gap으로 다시 세운다.
      pendingJoinGap = axis.gap;
      used += sliceH;
      startUnit = endUnit;
      // 다음 단위가 breakBefore 선언 라우팅 단위(청크 경로·안쪽 경계 하강)면 newPage를 생략한다 —
      // break **앞** 청크는 현재 낱장의 남은 공간을 이어받아야 최상위 동형이고(분할은 break 청크
      // 자리에서 placeChunks가 낸다), 여기서 낱장을 끊으면 그 앞 공간이 통째로 빈다. 그 외에는
      // 종전 그대로.
      if (startUnit < unitCount && !isBreakRoutedUnit(startUnit)) newPage();
    }
  };

  /**
   * 한 반복 단위의 **흐름 청크열**을 현재 낱장부터 순차 배치한다(순차 다경계 — 결정 2026-09-03
   * 심야). 규칙은 최상위 body 배치와 동형이다: 구간(비분할 청크)은 통짜, 경계 청크(table/repeat)는
   * 남은 공간에 안 들어가면 placeUnits로 행/항목 분할, 채움 표 청크는 최상위 채움 표 블록 동형
   * (자연높이+잔여 slack, keep-below로 다음 청크 1개 흡수, 행 분할이면 채움 생략 degrade).
   *
   * 낱장별 가시 청크 창은 `chunkRange` 채널로 나간다 — 걷기 중 각 배치가 `[c, c+1)`을 push하면
   * pushRangeEntry의 인접 병합이 한 낱장의 창을 하나로 합치고, 분할된 경계 청크는 자기 청크
   * 창 entry를 조상으로 실어 스팬하는 모든 낱장에 재방출한다. 창은 항상 연속이다(순차 걷기 +
   * 높이 0 청크도 창에 넣는다 — 건너뛰면 한 낱장에 창이 두 개가 되어 채널이 덮어쓴다).
   *
   * 단위 chrome(`uc.chrome` — branch 래퍼·template padding 등 청크 밖 잔여)은 낱장마다 한 번
   * 충전한다(leadHeight === contHeight라 첫/뒷 조각 구분이 없다). 청크 사이 간격은 0이 규약이다
   * (spacer 청크가 간격을 담당 — 최상위 body와 동형). forcedPlacement는 달지 않는다 — 청크 분할은
   * 강제 배치가 아니라 최상위 흐름과 같은 **구조적 흐름**이다(경계 청크 내부의 더 깊은 하강이
   * 다는 신호는 그대로).
   */
  const placeChunks = (
    uc: MeasuredUnitChunks,
    hostId: string,
    hostPath: string | undefined,
    unitRange: [number, number],
    unitChannel: UnitChannel,
    unitPath: string,
    ancestors: readonly {
      nodeId: string;
      path?: string;
      range: [number, number];
      channel: UnitChannel;
    }[],
    chromes: readonly AncestorChrome[],
  ): void => {
    const unitAncestors = [
      ...ancestors,
      { nodeId: hostId, path: hostPath, range: unitRange, channel: unitChannel },
    ];
    const allChromes: AncestorChrome[] = [
      ...chromes,
      { path: unitPath, leadHeight: Math.max(0, uc.chrome), contHeight: Math.max(0, uc.chrome) },
    ];
    // 이 낱장에서 아직 충전 안 된 chrome 합(placeUnits chromeH와 같은 회계 — 같은 맵 공유).
    const chunkChromeH = (): number =>
      allChromes.reduce((sum, ch) => {
        if (chromeChargedPage.get(ch.path) === page) return sum;
        const first = leadFirstPage.get(ch.path);
        return sum + (first === undefined ? ch.leadHeight : (ch.contHeight ?? 0));
      }, 0);
    /** 청크 c를 현재 낱장에 기록 — 조상·청크 창 entry 방출 + chrome 충전 표시(placeUnits 동형). */
    const noteOnPage = (c: number): void => {
      const suppressed = new Set<string>();
      for (const ch of allChromes) {
        const first = leadFirstPage.get(ch.path);
        if (first === undefined) leadFirstPage.set(ch.path, page);
        else if (first !== page) suppressed.add(ch.path);
        chromeChargedPage.set(ch.path, page);
      }
      const supp = (p?: string): true | undefined =>
        p !== undefined && suppressed.has(p) ? true : undefined;
      for (const a of unitAncestors) {
        pushRangeEntry(a.nodeId, a.path, a.range, a.channel, undefined, supp(a.path));
      }
      pushRangeEntry(hostId, unitPath, [c, c + 1], 'chunk', undefined, supp(unitPath));
    };
    const chunks = uc.chunks;
    for (let c = 0; c < chunks.length; c += 1) {
      const ch = chunks[c];
      const tmc = ch.table;
      const rmc = ch.repeat;
      const h = nodeH(ch);
      // pageBreak 'before'(청크) — 최상위 동형(빈 낱장 no-op, 높이 0 무시).
      if (ch.breakBefore === true && used > 0 && h > 0) newPage();

      // 채움 표 청크: 최상위 채움 표 블록 동형 — 통째 들어가면 자연높이 + 잔여 slack(빈 괘선 행)
      // + keep-below(다음 청크 1개 흡수). 안 들어가면 아래 행 분할로 fall-through(채움 생략).
      if (ch.pageFill === true && tmc) {
        const chromeAdd = chunkChromeH();
        const jg = pendingJoinGap;
        const fillNat = tmc.tableHeight;
        if (chromeAdd + jg + fillNat <= pageContentH - used + EPS) {
          const remaining = pageContentH - used - chromeAdd - jg - fillNat;
          const fillAll = ch.pageFillMode === 'fill-all';
          const cand = chunks[c + 1];
          const candH = nodeH(cand);
          const absorb = !fillAll && isKeepBelowAbsorbable(cand) && candH <= remaining + EPS;
          const slack = Math.round(remaining - (absorb ? candH : 0));
          noteOnPage(c);
          used += chromeAdd + jg + fillNat;
          pendingJoinGap = 0;
          if (slack > 0) {
            plan[page].push({ path: unitPath, nodeId: ch.id, fillSlack: slack });
            used += slack;
          }
          if (absorb) {
            noteOnPage(c + 1); // 인접 병합으로 창이 [c, c+2)로 확장된다.
            used += candH;
            c += 1;
          }
          if (c + 1 < chunks.length) newPage();
          continue;
        }
      }

      // 경계 청크(repeat/table)가 남은 공간에 통째로 안 들어감 → placeUnits로 분할 하강.
      // 청크 창 entry를 조상으로 실어 조각이 놓이는 모든 낱장에 창이 함께 나간다.
      // 채움 표 청크가 여기로 오면(위 통째 배치 미적합) fillSelf를 실어 **전 행이 한 조각으로
      // 담기는 낱장**(새 낱장 통짜 등)에는 채움이 살고, 행 분할 조각에는 부여되지 않는다(ⓒ 동형).
      const splitAxis =
        rmc && !tmc
          ? repeatAxis(rmc)
          : tmc
            ? tableAxis(tmc, ch.pageFill === true ? true : undefined)
            : null;
      if (splitAxis) {
        const son = tmc ? tmc.startOnNewPage : (rmc?.startOnNewPage ?? false);
        // breakBefore 청크 보유 단위를 품은 repeat 청크는 **들어가도** 하강한다(2026-09-05) —
        // placeUnits의 단위 라우팅이 그 단위를 청크 경로로 보내 신호가 소비된다(최상위 라우팅과
        // 같은 게이트, 깊이 무관 동형). 신호 없는 청크는 종전 그대로(회귀 0).
        const mustDescend = !tmc && rmc !== undefined && repeatHasBreakChunk(rmc);
        if (son && used > 0 && chunkChromeH() + pendingJoinGap + h > pageContentH - used + EPS) {
          newPage();
        }
        if (mustDescend || chunkChromeH() + pendingJoinGap + h > pageContentH - used + EPS) {
          placeUnits(
            splitAxis,
            ch.id,
            unitPath,
            [...unitAncestors, { nodeId: hostId, path: unitPath, range: [c, c + 1], channel: 'chunk' }],
            allChromes,
            false,
          );
          // 청크 사이 간격은 0(spacer 청크가 담당) — 하강이 남긴 슬라이스 gap을 물리지 않는다.
          pendingJoinGap = 0;
          continue;
        }
        // 통째로 들어가면 아래 공통 통짜 배치로 fall-through.
      }

      // 구간/통짜 청크 배치(최상위 통짜 배치 동형 + chrome 회계).
      const jg = pendingJoinGap;
      if (used > 0 && h > EPS && used + chunkChromeH() + jg + h > pageContentH + EPS) newPage();
      const chromeAdd = chunkChromeH();
      const jg2 = pendingJoinGap;
      noteOnPage(c);
      used += chromeAdd + jg2 + h;
      pendingJoinGap = 0;
    }
  };

  for (let i = 0; i < nodes.length; i += 1) {
    const n = nodes[i];
    const tm = n.table;

    // pageBreak 'before'(공통) — 이 노드 앞에서 무조건 새 페이지. 페이지가 비어 있으면 no-op,
    // 높이 0 노드(빈 branch/0행 repeat)는 무시(빈 분할 방지).
    if (n.breakBefore === true && used > 0 && nodeH(n) > 0) newPage();

    // 세로 채움 분배 run: 현재 노드가 fillSplitGroup을 가지면 같은 키의 인접 노드를 모아 페이지 남은
    // 예산을 grow 비율로 분배(각자 min-content=자연높이 하한, distributeVerticalFill). 예산 안에 run이
    // 통째로 들어가면 배정 높이만큼 표 빈행/노드 여백을 채우고, 합이 예산 초과(오버플로)면 채움을 생략
    // (degrade)하고 아래 기존 로직으로 흘려보낸다(각 노드가 자기 규칙대로 행 분할·페이지 넘김).
    // pageFill 단일 흡수의 다중 노드 일반화 — run은 페이지를 소진(뒤 노드는 다음 페이지부터).
    if (n.fillSplitGroup != null) {
      // run = 같은 group의 첫~마지막 노드 구간. 그 사이의 **마커 없는 노드는 자동으로 고정(grow 0)**
      // 멤버로 포함되어 예산에서 차감된다(작성자는 분배할 표에만 마커를 달면 되고, 여백/제목 등 사이
      // 노드는 자동 고정 — 실수로 조용히 깨지지 않음). 종료: 다른 group 마커 또는 breakBefore(공통
      // 페이지 분할)를 만나면 그 앞까지가 구간(마지막 group 노드 뒤의 마커 없는 노드는 run 밖 = keep-below).
      let lastGroupIdx = i;
      for (let j = i + 1; j < nodes.length; j += 1) {
        const m = nodes[j];
        if (m.breakBefore === true) break; // 공통 페이지 분할은 run 종료
        if (m.fillSplitGroup === n.fillSplitGroup) lastGroupIdx = j;
        else if (m.fillSplitGroup != null) break; // 다른 채움 그룹 → run 종료
        // 마커 없는 노드는 계속 스캔(다음 group 멤버가 있으면 그 사이의 고정 멤버로 흡수).
      }
      const run = nodes.slice(i, lastGroupIdx + 1);
      const dataH = run.map((m) => nodeH(m));
      const sumMin = dataH.reduce((a, b) => a + b, 0);
      const pageRemaining = pageContentH - used;
      // keep-below 예약: run 다음의 "고정 블록"(여백·텍스트·정적표 등)을 **연속으로** 모아 같은 페이지
      // 바닥에 함께 유지한다 — 예산에서 그 합을 빼고 분배하므로 채움 표들이 "그 블록들을 제외한 영역"을
      // 나눈다(예: DLR B표 뒤 [여백 + 금일사고현황]을 함께 page1 바닥에 유지). 흐름/분할 경계
      // (repeat=흐름 콘텐츠 / 다른 fillSplit·pageFill / startOnNewPage·breakBefore)를 만나거나 더 넣으면
      // run이 안 들어가는 지점에서 멈춘다. fill-all 모드면 예약 없이 페이지 전부 소진(뒤 노드는 다음 페이지).
      const fillAll = run[0].pageFillMode === 'fill-all';
      const reserved: MeasuredNode[] = [];
      let reserveH = 0;
      if (!fillAll) {
        for (let j = i + run.length; j < nodes.length; j += 1) {
          const c = nodes[j];
          // 흐름/분할 경계는 예약 중단(자체 페이지 규칙 우선).
          if (
            c.fillSplitGroup != null ||
            c.pageFill === true ||
            c.repeat != null ||
            isStartOnNewPage(c)
          ) {
            break;
          }
          if (nodeH(c) + sumMin + reserveH > pageRemaining + EPS) break; // 더 넣으면 run 미적합
          reserved.push(c);
          reserveH += nodeH(c);
        }
      }
      const budget = pageRemaining - reserveH;
      // 예산 안에 run이 통째로 들어감 → 분배 + 빈행 채움. (안 들어가면 오버플로 → 아래 fall-through)
      if (budget > EPS && sumMin <= budget + EPS) {
        const alloc = distributeVerticalFill(
          budget,
          run.map((m, k) => ({
            min: dataH[k],
            // 그룹 멤버만 grow로 분배에 참여. 사이의 마커 없는 노드는 grow 0(고정 = min 그대로).
            grow: m.fillSplitGroup === n.fillSplitGroup ? Math.max(0, m.fillGrow ?? 1) : 0,
          })),
        );
        for (let k = 0; k < run.length; k += 1) {
          const m = run[k];
          const slack = alloc[k] - dataH[k];
          if (m.table) {
            // 표: 잔여 slack을 빈 괘선 행으로(렌더 fillSlacks). slack≤0이면 통짜 배치.
            plan[page].push(slack > EPS ? { nodeId: m.id, fillSlack: slack } : { nodeId: m.id });
          } else {
            // 비표(group/stack): outer 높이를 배정 높이로 강제(렌더 fillHeights). slack≤0이면 통짜.
            plan[page].push(
              slack > EPS ? { nodeId: m.id, fillHeight: dataH[k] + slack } : { nodeId: m.id },
            );
          }
          used += dataH[k] + Math.max(0, slack);
        }
        i += run.length - 1; // run 소진(단조 증가 → 무한루프 없음)
        for (const rc of reserved) {
          // 예약된 고정 블록들을 run 바로 뒤 같은 페이지에 순서대로 배치.
          plan[page].push({ nodeId: rc.id });
          used += nodeH(rc);
          i += 1; // 예약분 소진(단조 증가)
        }
        // 채움은 페이지를 소진 → 뒤에 노드가 남으면 다음 페이지에서 이어감(단일 pageFill 동형).
        if (i + 1 < nodes.length) newPage();
        continue;
      }
      // 오버플로(sumMin > budget) 또는 예산 없음(budget≤0): 채움 생략 → 아래 기존 로직으로 fall-through
      // (fillSplitGroup 무시, 이 노드 n을 표/repeat/통짜 규칙대로 처리. 다음 run 멤버도 각자 재평가).
    }

    // 채움 표(pageFill 표): 표 자연높이 뒤 잔여 slack을 렌더가 빈 괘선 행으로 페인팅하도록 전달.
    // keep-together/예약 바닥 블록/fill-all은 비표 채움 노드와 동일 규칙. 표가 새 페이지에도 통째로
    // 안 들어가면(오버사이즈) 채움을 생략(degrade)하고 아래 기존 행 분할 로직으로 진행한다
    // (결정 기록 2026-07-02-composer-page-fill-node degrade 규칙과 동형 — planRowPages류 미접촉).
    if (n.pageFill === true && n.fillSplitGroup == null && tm) {
      const fillNat = tm.tableHeight;
      const fillAll = n.pageFillMode === 'fill-all';
      // pageFill 표는 keep-together 점프를 하지 않는다. 남은 공간에 들어가면 그 자리부터 채우고(아래 if),
      // 안 들어가면 통째로 새 페이지로 옮기지 않고 현재 페이지 남은 공간부터 행 분할 + 마지막 세그먼트
      // 채움으로 진행한다(fitting/오버사이즈 동일 — 아래 if를 건너뛰면 startOnNewPage/행 분할 경로로
      // fall-through). 그러지 않으면 앞 페이지 하단이 통째로 비고 표가 항상 새 페이지에서 시작한다
      // (2026-07-08 사용자 보고: fitting 표도 동일 증상). 루프 안전: 표 앞 used는 비-pageFill 노드에서
      // 나와 안정적이고 채움 행은 측정 제외라 fillSlack이 measured 높이를 안 바꿔 whole-fill↔split 경계
      // flip이 없다(slack은 Math.round 양자화로 float 노이즈까지 차단 — pagePlansEqual 정확비교 대비).
      if (fillNat <= pageContentH - used + EPS) {
        const avail = pageContentH - used;
        const remainingForBlock = avail - fillNat;
        // 예약 바닥 블록 = 다음 형제 1개(비표 채움 노드와 동일 — startOnNewPage 표는 흡수 제외).
        const cand = nodes[i + 1];
        const absorb =
          !fillAll && isKeepBelowAbsorbable(cand) && nodeH(cand) <= remainingForBlock + EPS;
        const acc = absorb ? nodeH(cand) : 0;
        const slack = Math.round(remainingForBlock - acc);
        // slack>0일 때만 전달(렌더가 빈 괘선 행 페인팅, 마지막 행이 끝자락 흡수 → 물리 확장 = slack).
        // slack≤0이면 통짜 배치와 동일(no-op).
        plan[page].push(slack > 0 ? { nodeId: n.id, fillSlack: slack } : { nodeId: n.id });
        used += fillNat + Math.max(0, slack);
        if (absorb) {
          plan[page].push({ nodeId: cand.id });
          used += acc;
          i += 1; // 흡수분(형제 1개)만큼 전진(단조 증가 → 무한루프 없음)
        }
        // 바닥 블록 다음에 남은 노드가 있으면 새 페이지에서 이어감(채움 표는 페이지를 소진).
        if (i + 1 < nodes.length) newPage();
        continue;
      }
      // 오버사이즈: 채움 생략(degrade) → 아래 행 분할 로직으로 fall-through(채움행 없이 정상 분할).
    }

    // 채움 노드(비표): 자연 높이 + slack으로 남은 공간을 채워 바닥 블록을 페이지 바닥에 flush.
    // (repeat이 pageFill이면 여기로 — 채움과 항목 분할은 상충하므로 채움 우선, 자연높이=단위합.)
    if (n.pageFill === true && n.fillSplitGroup == null && !tm) {
      const fillNat = nodeH(n);
      const fillAll = n.pageFillMode === 'fill-all';
      // keep-together: 채움노드(+keep-below면 바로 다음 노드)가 현재 페이지에 안 들어가고 이미
      // 쌓였으면 새 페이지. fill-all은 뒤를 안 데려가므로 채움 노드 자신만 검사.
      const nextH = fillAll ? 0 : nodeH(nodes[i + 1]);
      if (used > 0 && used + fillNat + nextH > pageContentH + EPS) newPage();

      const avail = pageContentH - used;
      const remainingForBlock = avail - fillNat;
      // 바닥 블록 = 채움노드의 다음 형제 1개(여럿은 group으로 묶음 — 결정 기록
      // 2026-07-02-composer-page-fill-node 결론 2. 암묵 greedy 흡수는 새 표를 바닥으로
      // 빨아들여 채움 높이를 줄이는 이탈이라 정정). fill-all은 흡수하지 않는다.
      // startOnNewPage 표/repeat도 흡수 대상에서 제외한다(자체 규칙대로 다음 페이지에서
      // 시작 — 흡수하면 채움 노드가 같은 페이지로 끌어들여 startOnNewPage 의도가 무효화됨).
      const cand = nodes[i + 1];
      const absorb =
        !fillAll && isKeepBelowAbsorbable(cand) && nodeH(cand) <= remainingForBlock + EPS;
      const acc = absorb ? nodeH(cand) : 0;
      const slack = remainingForBlock - acc;
      const fillH = fillNat + Math.max(0, slack);
      plan[page].push({ nodeId: n.id, fillHeight: fillH });
      used += fillH;
      if (absorb) {
        plan[page].push({ nodeId: cand.id });
        used += acc;
        // 흡수분(형제 1개)만큼 i 전진(단조 증가 → 무한루프 없음).
        i += 1;
      }
      // 바닥 블록 다음에 남은 노드가 있으면 새 페이지에서 이어감(채움 노드는 페이지를 소진).
      if (i + 1 < nodes.length) newPage();
      continue;
    }

    // repeat: 표 행 분할과 대칭인 항목 단위 분할. 분할 단위 = 다단이면 행(columns개 묶음), 아니면
    // 항목 1개(MeasuredRepeat.unitHeights). 항목은 통째 유지(중간 자르기 금지 — 카드 판독성).
    const rm = n.repeat;
    if (rm && !tm) {
      const totalH = repeatTotalH(rm);
      // startOnNewPage: 반복 전체가 남은 공간에 통째로 안 들어가고 이미 뭔가 쌓였으면 새 페이지에서 시작.
      if (rm.startOnNewPage && used > 0 && totalH > pageContentH - used + EPS) newPage();
      // 채움 표를 품은 단위(fillTable)는 **통째로 들어가도** 단위 배치로 간다 — 채움 단위마다
      // 낱장을 소진해야 하므로(뒤 단위·뒤 노드는 다음 낱장) 통짜 배치가 성립하지 않는다. 이 신호는
      // 측정이 단위 template에 pageFill 표가 정확히 1개일 때만 세우므로 기존 템플릿(반복 안
      // pageFill 미사용)은 이 분기에 오지 않는다(회귀 0).
      // breakBefore 선언 청크 보유 단위도 같은 축이다(2026-09-05, 선언 = template 파생 —
      // 측정 높이 게이트 금지, MeasuredRepeat.breakChunkDecl 주석) — 청크 안 pageBreak:'before'는
      // 무조건 분할이라 통짜 배치가 성립하지 않는다. 신호 없는 반복은 종전 그대로(회귀 0).
      // 반복이 남은 공간에 통째로 안 들어감 → 항목(단위) 분할.
      if (
        totalH > pageContentH - used + EPS ||
        rm.fillTable !== undefined ||
        repeatHasBreakChunk(rm)
      ) {
        // 최상위 층 진입 — 조상 없음(고정분 없음), 강제 분할로 내려온 층이 아님.
        // 앞 body 노드가 남긴 join gap은 이 반복과 무관하다(노드 사이 간격은 흐름이 담당).
        pendingJoinGap = 0;
        placeUnits(repeatAxis(rm), n.id, undefined, [], [], false);
        continue;
      }
      // 통째로 들어가면 아래 공통 통짜 배치로 fall-through(nodeH가 repeat 총높이).
    }

    // startOnNewPage: 표가 남은 공간에 통째로 안 들어가고 이미 뭔가 쌓였으면 새 페이지에서 시작.
    if (tm && tm.startOnNewPage && used > 0 && tm.tableHeight > pageContentH - used + EPS) {
      newPage();
    }

    // 표가 남은 공간에 통째로 안 들어감 → 행 분할.
    if (tm && tm.tableHeight > pageContentH - used + EPS) {
      // 채움 표(pageFill)면 마지막 세그먼트(전 행 소진)에서만 그 페이지 잔여를 빈 괘선 행으로 채운다
      // (+예약 바닥 블록 흡수). fillSlack은 정수 양자화(Math.round) — pagePlansEqual이 정확 비교라
      // float 노이즈면 수렴 실패(루프)를 막는다. 이 경로엔 오버사이즈 표뿐 아니라 fitting 표(남은 공간에
      // 안 들어가 keep-together 점프 없이 여기로 fall-through)도 온다 — 어느 쪽이든 분할 확정이고, 표 앞
      // used가 비-pageFill 노드에서 나와 안정적(채움 행은 측정 제외)이라 whole-fill↔split flip이 없다.
      // (2026-07-08, 결정 2026-07-08-...-split + fitting keep-together 제거)
      const isFillTable = n.pageFill === true && n.fillSplitGroup == null;
      const fillAllTable = n.pageFillMode === 'fill-all';
      let startRow = 0;
      while (startRow < tm.totalRows) {
        const avail = pageContentH - used;
        const base = tm.rowTop(startRow);
        // startRow부터 avail 안에 들어가는 최대 안전 endRow(keep-together 그룹 관통 회피).
        let endRow = startRow;
        for (let e = startRow + 1; e <= tm.totalRows; e += 1) {
          if (tm.rowTop(e) - base > avail + EPS) break;
          if (e === tm.totalRows || tm.safe[e]) endRow = e;
        }
        if (endRow <= startRow) {
          if (used > 0) {
            newPage();
            continue; // 새 페이지(avail=full)에서 재시도
          }
          // 새 페이지인데도 한 안전 단위가 페이지보다 큼(오버사이즈) → safe 무시 강제 raw-split.
          for (let e = startRow + 1; e <= tm.totalRows; e += 1) {
            if (tm.rowTop(e) - base > avail + EPS) break;
            endRow = e;
          }
          if (endRow <= startRow) endRow = startRow + 1; // 한 행조차 페이지 초과여도 최소 1행 전진
        }
        const segH = tm.rowTop(endRow) - tm.rowTop(startRow);
        if (isFillTable && endRow === tm.totalRows) {
          const availAfter = pageContentH - used - segH;
          const cand = nodes[i + 1];
          const absorb =
            !fillAllTable && isKeepBelowAbsorbable(cand) && nodeH(cand) <= availAfter + EPS;
          const slack = Math.round(availAfter - (absorb ? nodeH(cand) : 0));
          plan[page].push(
            slack > EPS
              ? { nodeId: n.id, rowRange: [startRow, endRow], fillSlack: slack }
              : { nodeId: n.id, rowRange: [startRow, endRow] },
          );
          used += segH + Math.max(0, slack);
          if (absorb) {
            plan[page].push({ nodeId: cand.id });
            used += nodeH(cand);
            i += 1; // 흡수분(형제 1개)만큼 전진 — for 헤더 i+=1과 합쳐 표+바닥블록 뒤로.
          }
          if (i + 1 < nodes.length) newPage();
          break; // 표 전 행 배치 완료 → while 종료(아래 for continue).
        }
        plan[page].push({ nodeId: n.id, rowRange: [startRow, endRow] });
        used += segH;
        startRow = endRow;
        if (startRow < tm.totalRows) newPage();
      }
      continue;
    }

    // 노드/표/repeat 통째 배치.
    const h = nodeH(n);
    // ⚠️ 높이 0 노드(빈 branch·0행 repeat)는 **절대 새 페이지를 만들지 않는다** — 차지하는 게
    // 없으니 넘칠 수도 없다. 이 가드가 없으면 앞 노드가 오버사이즈였을 때(항목 하나가 페이지보다
    // 큰 사진 묶음 등) used가 pageContentH를 넘긴 채 남아, 뒤따르는 빈 노드가 `used + 0 >
    // pageContentH`에 걸려 **내용 없는 마지막 페이지**를 만든다(2026-08-18 prod 실사고:
    // 안전점검보고서 끝에 빈 장. 우수사례 미표시 branch가 그 빈 노드였다).
    // breakBefore 가드(위 nodeH(n) > 0)와 같은 규약을 통짜 배치 경로에도 적용하는 것이다.
    if (used > 0 && h > EPS && used + h > pageContentH + EPS) newPage();
    plan[page].push({ nodeId: n.id });
    used += h;
  }

  return plan;
}

/** 세로 채움 분배 자식 1개의 입력(순수). */
export interface FillChild {
  /** min-content 높이(px) — 데이터 행 등 하한(그 이하로 줄지 않음). 음수는 0으로 클램프. */
  min: number;
  /** grow 비율(flex-grow, flex-basis 0 기준). 0이면 확장분을 안 받음(min만). 음수는 0으로 클램프. */
  grow: number;
}

/**
 * 세로 채움 분배(순수) — 부모 예산(px)을 자식들에게 CSS flexbox `flex: <grow> 1 0`(flex-basis 0)
 * 의미로 분배한다. 각 자식의 이상 배정 = `예산 × grow_i / Σgrow`(basis 0이라 min이 아니라 grow 비율로
 * 시작), 단 각자 `min`을 **하한**으로 존중한다. 이상 배정이 min보다 작은 자식은 min으로 **동결**하고
 * 남은 예산을 나머지 자식에게 재분배한다(flexbox 'resolve flexible lengths' min-violation 해소 루프
 * 동형 — 매 라운드 최소 1개 동결하므로 최대 자식 수만큼 반복).
 *
 * - 기본(둘 다 여유): budget 20, A{min 0,grow 1}·B{min 0,grow 1} → [10, 10](반씩).
 * - 한쪽 초과: A 콘텐츠가 14행(min 14) → A=14 동결, 남은 6을 B가 → [14, 6](요구 예시와 일치).
 * - 양쪽 초과(Σmin > budget): 각자 min 반환 → 합 > budget → **오버플로**(호출측이 페이지 넘김 처리).
 * - Σgrow === 0: 확장 대상 없음 → 각자 min(남는 예산은 미사용 빈 공간).
 *
 * 반환: 자식별 배정 높이(px), 길이 = children.length. 합 = max(budget, Σmin).
 * (빈행 채움은 이 배정 높이에서 각 표의 데이터행 높이를 뺀 slack으로 산출 — P2.)
 */
export function distributeVerticalFill(budget: number, children: FillChild[]): number[] {
  const n = children.length;
  if (n === 0) return [];
  const mins = children.map((c) => Math.max(0, c.min));
  const grows = children.map((c) => Math.max(0, c.grow));
  // grow가 전부 0 → 분배 대상 없음. 각자 min(남는 예산은 미사용).
  if (grows.every((g) => g === 0)) return mins.slice();

  const result = new Array<number>(n).fill(0);
  const frozen = new Array<boolean>(n).fill(false);
  // min-violation 해소 루프: 미동결 자식에 남은 예산을 grow 비율로 잠정 배정 → min 미달 자식을 min으로
  // 동결하고 재분배. 위반이 없는 라운드에서 종료(그때의 잠정 배정이 확정). 매 라운드 ≥1 동결 → 유한.
  for (let guard = 0; guard <= n; guard += 1) {
    let remaining = budget;
    let growSum = 0;
    for (let i = 0; i < n; i += 1) {
      if (frozen[i]) remaining -= result[i];
      else growSum += grows[i];
    }
    // 미동결 자식의 grow 합이 0(남은 게 전부 grow 0) → 남은 예산 배정 대상 없음, 미동결은 min으로.
    if (growSum <= 0) {
      for (let i = 0; i < n; i += 1) if (!frozen[i]) result[i] = mins[i];
      break;
    }
    let violated = false;
    for (let i = 0; i < n; i += 1) {
      if (frozen[i]) continue;
      const alloc = (remaining * grows[i]) / growSum;
      if (alloc < mins[i]) {
        result[i] = mins[i];
        frozen[i] = true;
        violated = true;
      } else {
        result[i] = alloc; // 잠정 — 이번 라운드에 동결이 없으면 확정.
      }
    }
    if (!violated) break;
  }
  return result;
}

/**
 * 두 페이지 계획(PagedItem[][])이 배치상 동일한지 얕은 비교 — measure()가 setState(리렌더) 여부를 판정.
 * 페이지 수/각 페이지 항목 수가 다르면 즉시 false, 같으면 항목별 (nodeId, rowRange, itemRange, fillHeight,
 * fillSlack)만 비교한다(JSON.stringify 전체 직렬화 회피 — 매 커밋/keystroke·측정마다 도는 경로라 비용
 * 절감). 값 동일=참조 유지=리렌더 억제(수렴). admin 편집 캔버스 measure()와 web 상세 낱장 뷰가 공유.
 */
/** createPlanConvergenceGuard 반환 — 측정→계획 루프의 순환(진동) 차단기. */
export interface PlanConvergenceGuard {
  /**
   * 새 계획(next)을 수용할지 판정. 수용 시 직전 계획(prev)을 순환 감지 히스토리에 쌓는다.
   *  - prev와 동일: 수렴 — false(리렌더 불필요) + 히스토리 리셋.
   *  - 히스토리의 과거 계획과 동일: **순환(진동) 감지 — false(현재 계획 유지, setState 중단)**.
   *  - 그 외: true(수용).
   */
  shouldAccept(prev: PagedItem[][], next: PagedItem[][]): boolean;
  /** 히스토리 리셋 — body(내용)가 바뀌면 호출측이 불러 정당한 "과거 계획 복귀"(undo 등)를 차단하지 않게 한다. */
  reset(): void;
}

/**
 * 페이지 계획 수렴 가드 — 측정→계획→렌더→재측정 루프가 **주기 진동**할 때 무한 setState를 차단한다.
 *
 * 배경(2026-07-27 실사고, form-import 초안 12x5 표): 어떤 행이 위 행들의 rowspan에 **전부 덮여
 * 시작 셀이 0개**인 병합 격자(HWPX 좌표맵에서 합법)는, 브라우저가 rowspan 셀 높이를 행 트랙에
 * 분배하는 방식이 **렌더된 계획(통짜/분할)에 따라 달라져** 측정 행높이가 계획마다 플립한다
 * (실측: 행높이 0↔157px, 표높이 1074↔1231px). 표가 페이지 경계에 걸치면 통짜↔분할 계획이 서로를
 * 유발해 무한 진동 → React "Maximum update depth exceeded" 크래시. 측정 높이가 계획에 의존하는 한
 * 계획기 쪽에서 근본 제거가 불가능하므로, **직전 몇 개의 수용 계획과 같은 계획으로 되돌아오면
 * 진동으로 판정하고 현재 계획을 유지**한다(둘 중 하나로 안정 — 시각적으로 유효한 배치).
 *
 * 사용(호스트 공통 — admin PagedEditBody / rc PagedComposerView):
 *   const guard = useRef(createPlanConvergenceGuard()).current;
 *   // body 변경 시 guard.reset() (undo로 과거 계획과 같아지는 정당한 복귀 허용)
 *   if (guard.shouldAccept(pagePlanRef.current, plan)) { pagePlanRef.current = plan; setPagePlan(plan); }
 * ⚠️ setState 함수형 updater 안에서 호출 금지(StrictMode 이중 호출 시 히스토리 오염) — ref 미러 사용.
 */
export function createPlanConvergenceGuard(historyCap = 4): PlanConvergenceGuard {
  let history: PagedItem[][][] = [];
  return {
    shouldAccept(prev, next) {
      if (pagePlansEqual(prev, next)) {
        history = [];
        return false;
      }
      if (history.some((p) => pagePlansEqual(p, next))) return false;
      history.push(prev);
      if (history.length > historyCap) history.shift();
      return true;
    },
    reset() {
      history = [];
    },
  };
}

export function pagePlansEqual(a: PagedItem[][], b: PagedItem[][]): boolean {
  if (a.length !== b.length) return false;
  for (let p = 0; p < a.length; p += 1) {
    const pa = a[p];
    const pb = b[p];
    if (pa.length !== pb.length) return false;
    for (let i = 0; i < pa.length; i += 1) {
      const ia = pa[i];
      const ib = pb[i];
      if (
        ia.nodeId !== ib.nodeId ||
        // 인스턴스 경로도 배치 정체성의 일부 — 빠지면 "사진3의 발견 0~2"와 "사진4의 발견 0~2"가
        // 같은 계획으로 오판돼 setState가 억제되고 화면이 stale 계획에 머문다(수렴이 아니라 정지).
        ia.path !== ib.path ||
        ia.rowRange?.[0] !== ib.rowRange?.[0] ||
        ia.rowRange?.[1] !== ib.rowRange?.[1] ||
        ia.itemRange?.[0] !== ib.itemRange?.[0] ||
        ia.itemRange?.[1] !== ib.itemRange?.[1] ||
        // 청크 창도 배치 정체성 — 빼면 창만 다른 계획이 "같다"로 오판돼 화면이 stale에 머문다.
        ia.chunkRange?.[0] !== ib.chunkRange?.[0] ||
        ia.chunkRange?.[1] !== ib.chunkRange?.[1] ||
        ia.fillHeight !== ib.fillHeight ||
        ia.fillSlack !== ib.fillSlack ||
        // ⚠️ 새 두 필드도 비교에 넣는다. 근거는 두 갈래다:
        //  ① suppressLead는 **그 낱장에 사진이 나오는지**를 바꾼다 = 명백히 다른 배치. 빼면 두 계획이
        //     "같다"로 판정돼 setState가 억제되고 화면이 stale 계획에 머문다(수렴이 아니라 정지 —
        //     path를 비교에 넣은 것과 같은 이유).
        //  ② forcedPlacement는 렌더 채널이 아니지만, 비교에서 빼면 **덜 엄격해진다**. 순환 가드는
        //     "과거 계획과 같으면 진동"으로 판정해 setState를 멈추므로, 느슨한 비교는 서로 다른
        //     계획을 같다고 보아 **중간 계획에서 정지**시킬 수 있다(1차 때 의심된 증상). 필드를
        //     더 넣는 쪽이 오탐을 줄이는 안전한 방향이고, 최악이라도 계획 1회 추가 수용일 뿐이다.
        //     P6-2 배지가 stale 계획에 머물지 않는 효과는 덤이다.
        ia.forcedPlacement !== ib.forcedPlacement ||
        ia.suppressLead !== ib.suppressLead
      ) {
        return false;
      }
    }
  }
  return true;
}
